/**
 * The English pack. This is the type source: every other pack is checked
 * against its key set, so a missing or invented key is a typecheck failure
 * rather than a blank label discovered in production.
 *
 * Packs ship in the repo as the fallback; a `translations` table overlays
 * per-key overrides at runtime, so support can fix a string without a deploy
 * (`02-architecture.md` § i18n).
 */
export const en = {
  "app.name": "TOVI",
  "app.description": "Restaurant back office",
  "app.operatorName": "TOVI Operator",
  "app.operatorDescription": "Platform operator console",

  "nav.dashboard": "Dashboard",
  "nav.menu": "Menu",
  "nav.operations": "Operations",
  "nav.staff": "Staff",
  "nav.roles": "Roles",
  "nav.stations": "Stations",
  "nav.printers": "Printers",
  "nav.pairTill": "Pair a till",
  "nav.printLog": "Print log",
  "nav.receiptTemplates": "Receipt templates",
  "nav.banners": "Banners",
  "nav.notifications": "Alerts",
  "nav.operationLog": "Operation log",
  "nav.inventory": "Inventory",
  "nav.supplies": "Supplies",
  "nav.suppliers": "Suppliers",
  "nav.warehouses": "Warehouses",
  "nav.units": "Units",
  "nav.supplyCategories": "Supply categories",
  "nav.brands": "Brands",
  "nav.chain": "Chain",
  "nav.substores": "Stores",
  "nav.invoice": "Invoice",
  "nav.trading": "Trading",

  "staff.title": "Staff",
  "staff.description": "Who works here, what they may do, and who may use the till.",
  "staff.searchPlaceholder": "Search by name, username or number",
  "staff.allRoles": "Any role",
  "staff.noRole": "No role",
  "staff.allStatuses": "Any status",
  "staff.tillAccess": "Till access",
  "staff.allTillAccess": "Any till access",
  "staff.tillAllowed": "May use the till",
  "staff.tillDenied": "May not use the till",
  "staff.new": "New employee",
  "staff.edit": "Edit employee",
  "staff.record": "Record",
  "staff.name": "Name",
  "staff.username": "Username",
  "staff.number": "Staff number",
  "staff.role": "Role",
  "staff.cashRegister": "May use the till",
  "staff.cashRegisterHint":
    "Separate from their role. Somebody can take orders without being able to take money.",
  "staff.posPermissions": "Till permissions",
  "staff.inventoryPermissions": "Inventory permissions",
  "staff.inventoryHint": "Granted separately from till access.",
  "staff.deleteTitle": "Remove this employee?",
  "staff.deleteBody": "They can no longer sign in to the till. Orders they rang keep their name.",
  "staff.photo": "Photo",
  "staff.photoHint": "Shown beside their name in the list.",
  "staff.photoAdd": "Add photo",
  "staff.photoReplace": "Replace photo",
  "staff.photoRemove": "Remove photo",
  "staff.viewPhoto": "View photo",
  "staff.cropTitle": "Crop photo",
  "staff.cropHint": "Drag to reposition. Only what shows inside the circle is kept.",
  "staff.zoom": "Zoom",
  "staff.cropConfirm": "Use this photo",
  "staff.removePhotoTitle": "Remove this photo?",
  "staff.removePhotoBody": "Their initials show instead until somebody adds a new one.",

  "staff.setPassword": "Set password",
  "staff.password": "Password",
  "staff.passwordConfirm": "Confirm password",
  "staff.passwordHint":
    "At least eight characters. They sign in to the staff app with their username and this password.",
  "staff.passwordMismatch": "The two passwords do not match.",
  "staff.passwordSaved":
    "Password set. They have been signed out everywhere and can sign in with the new one.",

  "roles.title": "Roles",
  "roles.description": "What each kind of job is allowed to do.",
  "roles.new": "New role",
  "roles.members": "People",
  "roles.permissions": "Permissions",
  "roles.seeded": "Built in",
  "roles.granted": "{count} granted",
  "roles.record": "Role",
  "roles.code": "Code",
  "roles.note": "Notes",
  "roles.noneGranted": "Nothing granted.",
  "roles.catalogueFailed":
    "Could not load the permission catalogue, so each grant is shown by its key.",

  "stations.title": "Stations",
  "stations.description": "Where the work happens. Items route to a station, not to a printer.",
  "stations.new": "New station",
  "stations.record": "Record",
  "stations.name": "Name",
  "stations.nameHint": "What the people working there call it — Hot pass, Bar, Dessert.",
  "stations.printers": "Printers",
  "stations.noPrinters": "No printer",
  "stations.noPrintersHint": "A station with no printer still routes; it prints nothing.",
  "stations.deleteTitle": "Delete this station?",
  "stations.deleteBody":
    "Only a station nothing points at can go: the printers standing at it and the items routed to it have to move first.",
  "printers.title": "Printers",
  "printers.description": "Paired devices, and which station each one stands at.",
  "printers.new": "New printer",
  "printers.record": "Record",
  "printers.name": "Name",
  "printers.model": "Model",
  "printers.connection": "Connection",
  "printers.address": "Address",
  "printers.addressHint": "An IP and port for LAN, or a MAC address for Bluetooth.",
  "printers.role": "Prints",
  "printers.station": "Station",
  "printers.stationHint": "Everything routed to this station prints here.",
  "printers.stationNone": "Choose a station",
  "printers.copies": "Copies",
  "printers.bind": "Pair",
  "printers.unbind": "Unpair",
  "printers.status": "Status",
  "printers.deleteTitle": "Remove this printer?",
  "printers.deleteBody":
    "Anything routed to it stops printing. A printer that some item names as its only device at a station cannot go.",
  "printerRole.receipt": "Receipts",
  "printerRole.station": "Station tickets",
  "printerRole.label": "Labels",
  "printerStatus.unbound": "Not paired",
  "printerStatus.online": "Online",
  "printerStatus.offline": "Offline",
  "printerStatus.error": "Error",

  "printLog.title": "Print log",
  "printLog.description": "Every document sent to a printer, and whether it arrived.",
  "printLog.document": "Document",
  "printLog.printer": "Printer",
  "printLog.queued": "Queued",
  "printLog.attempts": "Attempts",
  "printLog.reprint": "Print again",
  "printLog.failedHint": "A station ticket that failed is an order nobody is making.",
  "printLog.record": "Print job",
  "printLog.reference": "Reference",
  "printLog.printed": "Printed",
  "printLog.failure": "Why it failed",
  "printJobStatus.queued": "Waiting",
  "printJobStatus.printing": "Printing",
  "printJobStatus.printed": "Printed",
  "printJobStatus.failed": "Failed",
  "printJobStatus.cancelled": "Cancelled",
  "documentType.customer_receipt": "Customer receipt",
  "documentType.station_ticket": "Station ticket",
  "documentType.label": "Label",
  "documentType.shift_report": "Shift report",
  "documentType.drawer_kick": "Drawer opened",

  "templates.title": "Receipt templates",
  "templates.description": "What prints, in which language, and how it is sent to the printer.",
  "templates.new": "New template",
  "templates.language": "Language",
  "templates.lineWidth": "Characters per line",
  "templates.lineWidthHint": "32 on 58mm paper, 48 on 80mm.",
  "templates.renderMode": "Sent as",
  "templates.text": "Text",
  "templates.raster": "Image",
  "templates.auto": "Decide by language",
  "templates.rasterForced": "This language prints as an image",
  "templates.rasterReason.complexScript":
    "{script} cannot be printed from a thermal printer's built-in fonts, so this receipt is sent as an image. That is slower over Bluetooth than over a cable.",
  "templates.rasterReason.templateChoice": "This template always prints as an image.",
  "templates.preview": "Preview",
  "templates.showLogo": "Show the logo",
  "templates.headerText": "Header",
  "templates.footerText": "Footer",
  "templates.showTaxBreakdown": "Show the tax breakdown",
  "templates.showTaxRegistration": "Show tax registration details",
  "templates.showItemCodes": "Show item codes",
  "templates.showServiceCharge": "Show the service charge",
  "templates.showSecondCurrency": "Show the second currency",
  "templates.showQrCode": "Show a QR code",

  "banners.title": "Banners",
  "banners.description": "Adverts on the QR ordering menu.",
  "banners.new": "New banner",
  "banners.record": "Record",
  "banners.image": "Image address",
  "banners.link": "Link",
  "banners.schedule": "Shows",
  "banners.startsAt": "From",
  "banners.endsAt": "Until",
  "banners.live": "Showing now",
  "banners.notLive": "Not showing",
  "banners.deleteTitle": "Delete this banner?",
  "banners.deleteBody": "It comes off the QR ordering menu straight away.",

  "alerts.title": "Alerts",
  "alerts.description": "What the team is told about, and how.",
  "alerts.push": "Push notification",
  "alerts.voice": "Spoken on the till",
  "alerts.event": "Event",
  "alerts.language": "Spoken in",
  "alerts.repeat": "Repeat",
  "alerts.repeatHint": "A busy station is a noisy place.",
  "event.order.new": "A new order arrives",
  "event.order.paid": "An order is paid",
  "event.order.cancelled": "An order is cancelled",
  "event.order.refunded": "An order is refunded",
  "event.print.failed": "A document fails to print",
  "event.shift.warning": "A shift needs attention",
  "event.stock.sold_out": "An item sells out",

  "pairTill.title": "Pair a till",
  "pairTill.description":
    "A till shows an eight-character code. Enter it here to approve or refuse it.",
  "pairTill.codeLabel": "Till code",
  "pairTill.lookUp": "Look up",
  "pairTill.sessionNotice": "The till will sign in on your account until somebody signs in at it.",
  "pairTill.approve": "Pair this till",
  "pairTill.deny": "Deny",
  "pairTill.approved": "The till is paired; it will continue on its own within a few seconds.",
  "pairTill.denied": "The till was refused.",
  "pairTill.pairAnother": "Pair another till",
  "pairTill.notFound": "No till is showing that code.",
  "pairTill.expired": "That code has expired.",
  "pairTill.forbidden": "You are not allowed to pair a till.",
  "pairTill.alreadyUsed": "This code has already been used.",
  "pairTill.claimedElsewhere": "Someone else is already looking at this code.",
  "pairTill.identify": "This code identifies a person, not a till. It cannot be approved here.",

  "operationLog.title": "Operation log",
  "operationLog.description":
    "Who changed what, and when. This record cannot be edited or deleted.",
  "operationLog.when": "When",
  "operationLog.who": "Who",
  "operationLog.what": "Action",
  "operationLog.entity": "Affected",
  "operationLog.fields": "Fields changed",
  "operationLog.record": "Entry",
  "operationLog.actorKind": "Acted as",
  "operationLog.entityKind": "Record type",
  "operationLog.recordId": "Record ID",
  "operationLog.fieldsHint":
    "The values before and after are kept with the entry and are not shown here.",
  "actorKind.merchant": "Merchant",
  "actorKind.operator": "Platform operator",

  "trading.title": "Trading",
  "trading.description": "Your hours, how orders behave, and what the terminals show.",
  "trading.hours": "Hours",
  "trading.openTime": "Opens",
  "trading.closeTime": "Closes",
  "trading.hoursHint":
    "Your hours decide which trading day a sale belongs to, and so shape every report.",
  "trading.status": "Store status",
  "trading.hoursLog": "Changes to your hours",
  "trading.ordering": "Ordering",
  "trading.payFirst": "Diners pay before the food arrives",
  "trading.requirePax": "Ask how many people are dining",
  "trading.cartSharing": "Several diners can share one order",
  "trading.autoCancel": "Cancel unaccepted orders after",
  "trading.minutes": "minutes",
  "trading.printing": "Printing",
  "trading.autoPrintReceipt": "Print the receipt automatically at checkout",
  "trading.staffTerminalPrinter": "The staff terminal prints straight to the Wi-Fi printer",
  "trading.staffTerminalPrinterHint":
    "Turn this on only when the till is switched off. With both connected, the till stops printing receipts.",
  "trading.display": "What the terminals show",
  "trading.displayTables": "Show the table plan",
  "delivery.title": "Delivery platforms",
  "delivery.description": "The platforms you sell through, and what each takes.",
  "delivery.platforms": "Platforms",
  "delivery.name": "Shown as",
  "delivery.commission": "Commission",
  "delivery.commissionHint": "A percentage. 15 means 15%.",
  "delivery.noneInMarket": "No delivery platforms are available in this market yet.",
  "trading.delivers": "Take-away can be delivered",
  "trading.deliveryFee": "Delivery fee",
  "trading.autoAccept": "Accept without asking",
  "trading.autoAcceptHint":
    "An order on a ticked channel goes straight to the stations. Everything else waits for someone to look.",
  "trading.showItemSales": "Show diners how many have sold",
  "trading.voiceBroadcast": "Read new orders aloud at the till",
  "orderSource.cashier": "Till",
  "orderSource.staff_app": "Staff app",
  "orderSource.qr": "QR ordering",
  "orderSource.delivery": "Delivery",
  "orderSource.back_office": "Back office",
  "trading.showItemImages": "Show item photos on the till",
  "trading.showRemainingStock": "Show remaining stock",
  "trading.geofence": "Ordering distance",
  "trading.geofenceHint":
    "Diners must be within this many metres to order. The nearest allowed is {min}.",
  "trading.pickupNumbers": "Pick-up numbers",
  "trading.pickupHint": "Cycles daily between these numbers. Your market allows {min} to {max}.",
  "chain.title": "Stores",
  "chain.description": "Every location in the group, and what each one may configure for itself.",
  "chain.new": "Add a store",
  "chain.headOffice": "Head office",
  "chain.branch": "Branch",
  "chain.name": "Name",
  "chain.address": "Address",
  "chain.slug": "Web address",
  "chain.slugHint": "Used by the QR ordering site. Lower case, letters, numbers and hyphens.",
  "chain.phone": "Phone",
  "chain.timezone": "Time zone",
  "chain.hours": "Hours",
  "chain.currency": "Currency",
  "chain.items": "Items",
  "chain.tables": "Tables",
  "chain.staff": "Staff",
  "chain.record": "Record",
  "chain.switchTo": "Switch to this store",
  "chain.capabilities": "What this store may configure",
  "chain.capabilitiesHint":
    "Withhold one and this store keeps the permission but cannot act on it. Head office configures it instead.",
  "chain.capabilitiesSaved": "Saved.",

  "shell.switchStore": "Switch store",
  "nav.chainMenu": "Menu push",
  "nav.chainUsers": "Store access",
  "action.done": "Done",
  "chainMenu.title": "Head office menu",
  "chainMenu.description":
    "The menu head office owns. Push an item to a location and the two stay linked, until that location edits its own copy.",
  "chainMenu.sku": "SKU",
  "chainMenu.categories": "Categories",
  "chainMenu.price": "Price",
  "chainMenu.status": "Status",
  "chainMenu.pushedTo": "Locations",
  "chainMenu.record": "Record",
  "chainMenu.push": "Push to stores",
  "chainMenu.pushCount": "Push {count} items",
  "chainMenu.pushTitle": "Push {count} items",
  "chainMenu.pushHint":
    "Pick the locations to push to. An item a location added itself is never touched, and prices are only pushed between stores trading in the same currency.",
  "chainMenu.pushing": "Pushing…",
  "chainMenu.noTargets": "This chain has no locations other than head office.",
  "chainMenu.overwriteLocal": "Overwrite locations' own edits",
  "chainMenu.overwriteLocalHint":
    "Off by default. A location that has edited a pushed item keeps its version, and the push reports it as skipped.",
  "chainMenu.result": "What each location received",
  "chainMenu.resultLine": "{created} added, {updated} updated.",
  "chainMenu.resultSkipped": "{count} left alone — edited locally.",
  "chainUsers.title": "Store access",
  "chainUsers.description":
    "Which locations each back-office account can work in. New accounts reach the whole chain until narrowed here.",
  "chainUsers.name": "Person",
  "chainUsers.username": "Username",
  "chainUsers.email": "Email",
  "chainUsers.role": "Role",
  "chainUsers.record": "Record",
  "chainUsers.reach": "Reaches",
  "chainUsers.stores": "Locations",
  "chainUsers.wholeChain": "The whole chain",
  "chainUsers.wholeChainHint": "Every location, including any added later.",
  "chainUsers.namedStores": "Only these locations",
  "chainUsers.namedStoresHint":
    "They can sign in to, switch between and report on these locations, and no others.",
  "chainUsers.storeCount": "{count} locations",
  "chainUsers.you": "You",
  "capability.configure_coupons": "Its own coupons",
  "capability.configure_discount_promos": "Its own discount promotions",
  "capability.configure_topup_tiers": "Its own top-up tiers",
  "capability.custom_topup_amounts": "Top-up amounts keyed in by hand",

  "settings.invoice.title": "Invoice numbering",
  "settings.invoice.description":
    "The number printed on a bill, and the tax identity printed above it.",
  "settings.invoice.numbering": "Numbering",
  "settings.invoice.enabled": "Print an invoice number on receipts",
  "settings.invoice.prefix": "Prefix",
  "settings.invoice.dateType": "Restart the sequence",
  "settings.invoice.dateType.none": "Never — one continuous sequence",
  "settings.invoice.dateType.year": "Each year",
  "settings.invoice.dateType.date": "Each day",
  "settings.invoice.nextNumber": "Next number",
  "settings.invoice.rangeHint": "Your market allows {min} to {max}.",
  "settings.invoice.preview": "The next invoice will read",
  "settings.invoice.continuity":
    "Numbers print in an unbroken sequence and can only move forward. A change applies to new orders; nothing already printed is renumbered.",
  "settings.invoice.taxIdentity": "Tax details on the receipt",
  "settings.invoice.taxIdentityHint":
    "Printed above the items when the receipt template is set to show them.",
  "settings.invoice.companyName": "Company name",
  "settings.invoice.registrationNumber": "Tax registration number",
  "settings.invoice.companyAddress": "Company address",
  "settings.invoice.deviceNumber": "Registered device number",
  "settings.invoice.deviceNumberHint":
    "Where the tax authority registers the till itself, its number belongs on every invoice. Leave empty if yours does not.",

  "tradingStatus.open": "Open",
  "tradingStatus.temporarily_closed": "Temporarily closed",
  "tradingStatus.permanently_closed": "Permanently closed",

  "nav.items": "Items",
  "nav.categories": "Categories",
  "nav.specifications": "Specification groups",
  "nav.toppings": "Toppings",
  "nav.tags": "Tags",
  "nav.tables": "Tables",
  "nav.settings": "Settings",
  "nav.storeProfile": "Store profile",
  "nav.tax": "Tax",
  "nav.serviceCharge": "Service charge",
  "nav.discountExclusions": "Discount exclusions",
  "nav.paymentMethods": "Payment methods",
  "nav.quickNotes": "Quick notes",

  "action.add": "Add",
  "action.view": "View",
  "action.edit": "Edit",
  "action.delete": "Delete",
  "action.save": "Save",
  "action.cancel": "Cancel",
  "action.saving": "Saving…",
  "action.select": "Choose one",
  "action.search": "Search",
  "action.clear": "Clear",
  "action.previous": "Previous",
  "action.next": "Next",
  "action.moveUp": "Move up",
  "action.moveDown": "Move down",
  "action.regenerate": "Regenerate",
  "action.close": "Close",
  "action.confirmDelete": "Delete",

  "list.empty": "Nothing here yet.",
  "list.noMatches": "Nothing matches that search.",
  "list.noFilterMatches": "Nothing matches those filters.",
  "list.showing": "{from}–{to} of {total}",
  "list.noResults": "No results",
  "list.pagination": "Pagination",
  "list.page": "Page {page}",
  "list.morePages": "More pages",
  "list.perPage": "Rows per page",
  "list.perPageOption": "{count} per page",
  "list.sortAscending": "sorted ascending",
  "list.sortDescending": "sorted descending",
  "list.loadFailed": "Could not load this list.",
  "list.saveFailed": "Could not save that.",

  "calendar.previousMonth": "Previous month",
  "calendar.nextMonth": "Next month",

  "dateRange.label": "Date range",
  "dateRange.apply": "Apply",
  "dateRange.today": "Today",
  "dateRange.yesterday": "Yesterday",
  "dateRange.last7Days": "Last 7 days",
  "dateRange.last30Days": "Last 30 days",
  "dateRange.monthToDate": "Month to date",
  "dateRange.lastMonth": "Last month",
  "dateRange.yearToDate": "Year to date",
  "dateRange.lastYear": "Last year",

  "items.title": "Items",
  "items.description": "Everything on the menu, with its price, modifiers and station routing.",
  "items.searchPlaceholder": "Search by name or code",
  "items.new": "New item",
  "items.edit": "Edit item",
  "items.record": "Record",
  "items.money": "Money",
  "items.name": "Name",
  "items.code": "Item code",
  "items.codeHint": "The SKU or PLU printed on a station ticket.",
  "items.price": "Price",
  "items.priceHint": "In {currency}.",
  "items.category": "Categories",
  "items.status": "Status",
  "items.recipe": "What this is made of",
  "items.recipeHint":
    "Linked supplies come out of stock when the bill is settled, not when the order is taken.",
  "items.soldOut": "Sold out",
  "items.autoSoldOut": "Let stock decide",
  "items.autoSoldOutHint":
    "Sells out on its own when a supply in its recipe runs out, and comes back when stock does. An item with no recipe is left alone.",
  "items.available": "Available",
  "items.madeToOrder": "Made to order",
  "items.stations": "Stations",
  "items.stationsHint":
    "Where this gets made. It can be more than one place, and each gets its own ticket.",
  "items.stationsNone": "No station",
  "items.stationPrinters": "Printers",
  "items.stationPrintersAll": "All printers at this station",
  "items.packingFee": "Packing fee",
  "items.kind": "Kind of item",
  "items.kindHint":
    "A plain dish, a set made of other dishes, a one-off, or something ordered ahead.",
  "items.kind.normal": "Ordinary dish",
  "items.kind.mealSet": "Meal set",
  "items.kind.temporary": "One-off",
  "items.kind.preOrder": "Ordered ahead",
  "items.pricingMethod": "Sold by",
  "items.pricing.unit": "The dish",
  "items.pricing.weight": "Weight",
  "items.wasPrice": "Was price",
  "items.deliveryPrice": "Delivery price",
  "items.deliveryPriceHint":
    "What this item costs on a delivery platform. Leave empty to sell it at the menu price. Platforms take a commission, so most menus are marked up here.",
  "items.wasPriceHint": "Shown struck through beside the price. Leave empty for no offer.",
  "items.pricedAtTill": "Priced at the till each day",
  "items.mealSet": "What is in the set",
  "items.mealSetHint":
    "Groups of choices. Each option is another dish on this menu, priced by what it adds.",
  "items.mealSetGroup": "Choice",
  "items.mealSetPicks": "Pick",
  "items.mealSetPick": "Choose a dish",
  "items.mealSetDelta": "Extra",
  "items.mealSetAddOption": "Add a dish",
  "items.mealSetAddGroup": "Add a choice",
  "items.deleteTitle": "Delete this item?",
  "items.deleteBody":
    "It disappears from the menu. Past orders keep their own record of what was sold, so nothing in your history changes.",
  "items.allCategories": "All categories",
  "items.allStatuses": "Any status",
  "items.uncategorised": "No category",
  "items.nameInLocale": "Name in {locale}",
  "items.translationHint": "Diners ordering in {locale} see this instead.",
  "items.modifiers": "Modifiers",
  "items.modifiersHint": "What a cashier is asked when this item is tapped.",
  "items.modifiersNone": "None",

  "categories.title": "Categories",
  "categories.description": "How the menu is grouped, and the order it appears in.",
  "categories.new": "New category",
  "categories.edit": "Edit category",
  "categories.record": "Record",
  "categories.name": "Name",
  "categories.itemCount": "Items",
  "categories.allStatuses": "Any status",
  "categories.deleteTitle": "Delete this category?",
  "categories.deleteBody": "A category with items in it cannot be deleted. Move them first.",

  "modifiers.specifications": "Specification groups",
  "modifiers.specificationsDescription": "Choices that change what is sold — size, hot or iced.",
  "modifiers.toppings": "Toppings",
  "modifiers.toppingsDescription": "Priced extras a diner can add.",
  "modifiers.tags": "Tags",
  "modifiers.tagsDescription":
    "Instructions for the station. They can carry a price if you charge for them.",
  "modifiers.record": "Record",
  "modifiers.options": "Options",
  "modifiers.optionName": "Name",
  "modifiers.optionPrice": "Price change",
  "modifiers.optionStatus": "Status",
  "modifiers.addOption": "Add an option",
  "modifiers.required": "A choice is required",
  "modifiers.selection": "Selection",
  "modifiers.single": "One only",
  "modifiers.multiple": "Any number",
  "modifiers.optionDefault": "Ticked by default",
  "modifiers.storeDefault": "Add to every new item",
  "modifiers.usedBy": "Used by",
  "modifiers.deleteTitle": "Delete this group?",
  "modifiers.deleteBody":
    "Items linked to it stop offering its options. Past orders keep their own record of what was chosen, so nothing in your history changes.",

  "tables.title": "Tables",
  "tables.description": "The floor plan, and the QR code each table carries.",
  "tables.new": "New table",
  "tables.edit": "Edit table",
  "tables.record": "Record",
  "tables.name": "Table",
  "tables.area": "Area",
  "tables.capacity": "Covers",
  "tables.qrCode": "QR code",
  "tables.status": "Status",
  "tables.tabList": "Table list",
  "tables.tabFloorPlan": "Floor plan",
  "tables.viewsLabel": "Table views",
  "tables.searchPlaceholder": "Search tables…",
  "tables.count": "{count} tables",
  "tables.qrOrdering": "QR ordering",
  "tables.viewQr": "View QR",
  "tables.qrExplainer": "Guests can scan the QR code to view the menu and place orders.",
  "tables.qrUnavailable":
    "QR ordering has no public address configured yet, so there is nothing for a phone to scan.",
  "tables.qrFor": "QR code for {table}",
  "tables.printQr": "Print",
  "tables.deleteTitle": "Delete {name}?",
  "tables.deleteBody": "The table and its QR code go away. Past orders keep their record of it.",
  "tables.statusHint": "Occupancy is set by the till as guests are seated and served.",
  "tables.decreaseCovers": "Fewer covers",
  "tables.increaseCovers": "More covers",
  "tables.emptyTitle": "No tables yet",
  "tables.emptyBody":
    "Create your first table to start taking orders and generating table QR codes.",
  "tables.floorPlanTitle": "The floor plan is not built yet",
  "tables.floorPlanBody": "Tables and their areas are managed from the table list for now.",
  "tables.areas": "Areas",
  "tables.newArea": "New area",
  "tables.defaultArea": "Default area",
  "tables.regenerateQrTitle": "Give this table a new QR code?",
  "tables.regenerateQrBody":
    "Any printed code for this table stops working immediately. Print and place the new one before the next service.",
  "tables.tempCode": "Walk-in codes",
  "tables.tempCodeDescription":
    "Numbers for orders taken without a table — the counter, the bar, a delivery rider.",
  "tables.tempCodeEnabled": "Use walk-in codes",
  "tables.tempCodePrefix": "Prefix",
  "tables.tempCodeFrom": "From",
  "tables.tempCodeTo": "To",

  "settings.store.title": "Store profile",
  "settings.store.description": "Who you are, where you are, and when your day ends.",
  "settings.store.name": "Store name",
  "settings.store.address": "Address",
  "settings.store.phone": "Phone",
  "settings.store.timezone": "Timezone",
  "settings.store.businessDayCutoff": "Business day ends at",
  "settings.store.businessDayCutoffHint":
    "Takings after midnight count towards the day before, up to this time. A restaurant's day ends when service does.",
  "settings.store.statisticsBasis": "Reports count a sale under",
  "settings.store.statisticsBasisHint":
    "Which day every report files a sale under. Separate from the cutoff above: that decides which day a bill is stamped with, this decides which of the two stamped dates a report reads. A store can keep a 3am cutoff and still close its books on calendar months.",
  "settings.store.statisticsBasisBusinessDay": "The trading day, ending at the cutoff",
  "settings.store.statisticsBasisCalendarDay": "The calendar day, midnight to midnight",
  "settings.store.languages": "Languages",
  "settings.store.languagesHint":
    "Which languages this store's screens and receipts offer. Khmer receipts print as an image, because a thermal printer cannot render the script from a codepage.",
  "settings.store.currency": "Currency",
  "settings.store.primaryCurrency": "Primary currency",
  "settings.store.secondCurrency": "Second currency",
  "settings.store.exchangeRate": "Exchange rate",
  "settings.store.exchangeRateHint":
    "How many {second} to one {primary}. Changing it does not alter bills already settled.",
  "settings.store.rateHistory": "Rate history",
  "settings.store.changedBy": "Changed by",
  "settings.store.effectiveFrom": "From",

  "settings.tax.title": "Tax",
  "settings.tax.description": "How tax is calculated on a bill.",
  "settings.tax.rules": "Tax rules",
  "settings.tax.unset": "Set your rate",
  "settings.tax.newRule": "New rule",
  "settings.tax.editRule": "Edit rule",
  "settings.tax.code": "Code",
  "settings.tax.name": "Name",
  "settings.tax.rate": "Rate",
  "settings.tax.rateHint": "As a percentage. Two decimal places are kept exactly.",
  "settings.tax.inclusive": "Included in the price",
  "settings.tax.inclusiveHint":
    "Your menu prices already contain this tax. It is shown on the bill but not added to it.",
  "settings.tax.exclusiveHint": "This tax is added to the bill at the till.",
  "settings.tax.sequence": "Order",
  "settings.tax.appliesToCategories": "Only these categories",
  "settings.tax.appliesToCategoriesHint": "Leave empty to apply to everything.",
  "settings.tax.appliesToDiningStyles": "Only these service types",
  "settings.tax.appliesToServiceCharge": "Also taxes the service charge",
  "settings.tax.appliesToTableFee": "Also taxes the table fee",
  "settings.tax.chargeTax": "Charge tax",
  "settings.tax.chargeTaxHint":
    "Switching this off suspends collection and keeps your rules. It is not the same as a zero rate, which still records a tax line.",
  "settings.tax.label": "What you call it",
  "settings.tax.labelHint": "VAT, GST, Sales Tax — whatever appears on your receipts.",
  "settings.tax.deleteTitle": "Delete this tax rule?",
  "settings.tax.deleteBody":
    "It stops applying to new bills. Bills already settled keep the rate they were taxed at.",

  "settings.serviceCharge.title": "Service charge",
  "settings.serviceCharge.description": "What you add for service, and when.",
  "settings.serviceCharge.newRate": "New rate",
  "settings.serviceCharge.deleteTitle": "Delete this rate?",
  "settings.serviceCharge.deleteBody":
    "It stops being added to new bills. Bills already settled keep the service charge they were charged.",
  "settings.serviceCharge.rate": "Rate",
  "settings.serviceCharge.default": "Default",
  "settings.serviceCharge.appliesTo": "Applies to",
  "settings.serviceCharge.calculation": "Worked out",
  "settings.serviceCharge.onSubtotal": "On the whole bill",
  "settings.serviceCharge.perUnit": "Per item",
  "settings.serviceCharge.calculationHint":
    "Per item charges each unit and rounds each one, which can differ by a few cents from charging the bill as a whole.",
  "settings.serviceCharge.rounding": "Rounding",
  "settings.serviceCharge.tableFee": "Table fee",
  "settings.serviceCharge.tableFeeEnabled": "Charge a table fee",
  "settings.serviceCharge.tableFeeBasis": "Charged",
  "settings.serviceCharge.perHead": "Per cover",
  "settings.serviceCharge.perTable": "Per table",
  "settings.serviceCharge.amount": "Amount",
  "settings.tip.title": "Tipping",
  "settings.tip.description":
    "A tip is the guest's money passing to staff. A service charge is yours. They are different under the law and you may run both.",
  "settings.tip.enabled": "Offer a tip at the till",
  "settings.tip.suggested": "Suggested percentages",
  "settings.tip.suggestedHint": "Up to four, separated by commas. 18, 20, 22 is the usual trio.",
  "settings.tip.basis": "Suggest a percentage of",
  "settings.tip.basisHint":
    "Card terminals usually suggest on the total. The older convention is the subtotal.",
  "settings.tip.basisTotal": "The total, after tax",
  "settings.tip.basisSubtotal": "The subtotal, before tax",

  "settings.discountExclusions.title": "Discount exclusions",
  "settings.discountExclusions.description": "Items a whole-bill discount never touches.",
  "settings.discountExclusions.excluded": "Excluded",
  "settings.discountExclusions.selected": "{count} excluded",

  "settings.paymentMethods.title": "Payment methods",
  "settings.paymentMethods.description": "What the till offers, and in what order.",
  "settings.paymentMethods.enabled": "Offered",
  "settings.paymentMethods.provider": "Provider",
  "settings.paymentMethods.noProvider": "None",
  "settings.paymentMethods.add": "Add a payment method",
  "settings.paymentMethods.newName": "What the till should call it",
  "settings.paymentMethods.builtIn": "Built in",
  "settings.paymentMethods.builtInHint":
    "Built-in methods can be renamed or turned off, not deleted.",
  "settings.paymentMethods.nameHint": "Rename any method — the till and every report follow.",
  "settings.paymentMethods.deleteConfirm":
    "Delete this payment method? Past bills keep the name they were settled under.",

  "settings.quickNotes.title": "Quick notes",
  "settings.quickNotes.description": "Shortcuts a cashier taps instead of typing.",
  "settings.quickNotes.new": "New note",
  "settings.quickNotes.record": "Record",
  "settings.quickNotes.deleteTitle": "Delete this note?",
  "settings.quickNotes.deleteBody":
    "Cashiers stop being offered it. Orders it was already put on keep the text.",

  "diningStyle.dine_in": "Dine in",
  "diningStyle.take_away": "Takeaway",
  "diningStyle.pre_order": "Pre-order",
  "diningStyle.delivery": "Delivery",

  "status.enabled": "Active",
  "status.disabled": "Hidden",
  "tableStatus.free": "Free",
  "tableStatus.seated": "Seated",
  "tableStatus.ordered": "Ordered",
  "tableStatus.bill_requested": "Bill requested",

  "rounding.half_up": "Half up",
  "rounding.half_even": "Half to even",
  "rounding.half_down": "Half down",
  "rounding.up": "Always up",
  "rounding.down": "Always down",
  "rounding.ceiling": "Up",
  "rounding.floor": "Down",

  "inventory.supplies.title": "Supplies",
  "inventory.supplies.description": "What the stations buy, hold and consume.",
  "inventory.supplies.new": "New supply",
  "inventory.supplies.deleteTitle": "Delete this supply?",
  "inventory.supplies.deleteBody":
    "A supply that still has stock cannot be deleted — write it off first. Its batches " +
    "and every movement posted against it stay where they are.",
  "inventory.number": "Number",
  "inventory.record": "Record",
  "inventory.name": "Name",
  "inventory.otherName": "Also known as",
  "inventory.category": "Category",
  "inventory.brand": "Brand",
  "inventory.barcode": "Barcode",
  "inventory.barcodeHint": "Type it or scan it. One supply per barcode.",
  "inventory.stockUnit": "Stock unit",
  "inventory.stockUnitHint": "What this is counted in. It cannot be changed once stock exists.",
  "inventory.purchaseUnit": "Purchase unit",
  "inventory.salesUnit": "Sales unit",
  "inventory.minimumStock": "Warn below",
  "inventory.inStock": "In stock",
  "inventory.belowMinimum": "Low",
  "inventory.suppliers.title": "Suppliers",
  "inventory.suppliers.description": "Who you buy from, and what they charge.",
  "inventory.suppliers.new": "New supplier",
  "inventory.contactPerson": "Contact",
  "inventory.contactNumber": "Phone",
  "inventory.address": "Address",
  "inventory.nickname": "Short name",
  "inventory.supplierPrices": "Supplies",
  "inventory.warehouses.title": "Warehouses",
  "inventory.warehouses.description": "Where stock sits, and which batch leaves first.",
  "inventory.warehouses.new": "New warehouse",
  "inventory.isDefault": "Deduct sales from here",
  "inventory.valuationPolicy": "Which batch leaves first",
  "inventory.valuation.fifo": "Oldest first (FIFO)",
  "inventory.valuation.lifo": "Newest first (LIFO)",
  "inventory.valuationHint":
    "This decides what an outbound costs, not the order things are listed in.",
  "inventory.units.title": "Units",
  "inventory.units.description": "How supplies are measured, and how one measure becomes another.",
  "inventory.units.new": "New unit",
  "inventory.dimension": "Measures",
  "inventory.dimension.mass": "Weight",
  "inventory.dimension.volume": "Volume",
  "inventory.dimension.count": "Count",
  "inventory.seeded": "Built in",
  "inventory.conversions": "Conversions",
  "inventory.conversions.new": "New conversion",
  "inventory.conversionFrom": "One of",
  "inventory.conversionTo": "makes",
  "inventory.factor": "How many",
  "inventory.appliesTo": "Applies to",
  "inventory.everySupply": "Every supply",
  "inventory.categories.title": "Supply categories",
  "inventory.categories.description": "Two levels: a category, and the sub-categories beneath it.",
  "inventory.categories.new": "New category",
  "inventory.categories.deleteTitle": "Delete this category?",
  "inventory.categories.deleteBody":
    "A category with supplies or sub-categories in it cannot be deleted. Move them first.",
  "inventory.parent": "Sits under",
  "inventory.topLevel": "Nothing — it is top level",
  "inventory.subCategories": "Sub-categories",
  "inventory.supplyCount": "Supplies",
  "inventory.brands.title": "Brands",
  "inventory.brands.description": "Whose product it is, where that matters.",
  "inventory.brands.new": "New brand",
  "inventory.brands.deleteTitle": "Delete this brand?",
  "inventory.brands.deleteBody":
    "A brand still on a supply cannot be deleted. Clear it from those supplies first.",
  "inventory.description": "Notes",

  "nav.stock": "Stock",
  "nav.purchases": "Purchases",
  "nav.inbound": "Inbound",
  "nav.outbound": "Outbound",
  "nav.stockCounts": "Stock counts",
  "stock.title": "Stock",
  "stock.description": "What is on hand, what it is worth, and what is running low.",
  "stock.warehouse": "Warehouse",
  "stock.quantity": "On hand",
  "stock.value": "Value",
  "stock.onlyLow": "Only what is running low",
  "stock.batches": "Batches",
  "stock.usedIn": "Used in",
  "stock.batchNo": "Batch",
  "stock.received": "Received",
  "stock.remaining": "Remaining",
  "stock.unitCost": "Cost each",
  "stock.receivedAt": "Taken in",
  "stock.expiresAt": "Use by",
  "stock.drawOrder": "Drawn in this order",
  "stock.drawOrderReverse": "Drawn in reverse of this order",
  "purchases.title": "Purchases",
  "purchases.description": "What has been ordered, and what has arrived.",
  "purchases.new": "New purchase",
  "purchases.type": "Bought from",
  "purchases.type.supplier": "A supplier",
  "purchases.type.market": "The market",
  "purchases.type.head_office": "Head office",
  "purchases.supplier": "Supplier",
  "purchases.total": "Total",
  "purchases.notifyInbound": "Send to the warehouse",
  "purchases.receiveNow": "Take in now",
  "purchases.void": "Void",
  "purchases.voidReason": "Why",
  "inbound.title": "Inbound",
  "inbound.description": "Deliveries waiting to be checked, and what has been taken in.",
  "inbound.new": "New inbound",
  "inbound.type": "Reason",
  "inbound.type.supplier": "From a supplier",
  "inbound.type.market": "From the market",
  "inbound.type.item_return": "Returned",
  "inbound.type.overstock": "Found in stock",
  "inbound.type.transfer": "Transferred in",
  "inbound.approve": "Take it in",
  "inbound.reject": "Reject",
  "inbound.rejectReason": "Why",
  "inbound.batchNo": "Batch",
  "inbound.unitCost": "Cost each",
  "inbound.freeQuantity": "Free",
  "outbound.title": "Outbound",
  "outbound.description": "What has left, why, and what it cost.",
  "outbound.new": "New outbound",
  "outbound.type": "Reason",
  "outbound.type.loss": "Loss",
  "outbound.type.sales": "Sold on",
  "outbound.type.transfer": "Transfer",
  "outbound.type.item_sales": "Sold as a dish",
  "outbound.type.shortage": "Shortage",
  "outbound.destination": "To warehouse",
  "outbound.cost": "Cost",
  "outbound.shortfall": "Short by",
  "outbound.customer": "Customer",
  "outbound.salesAmount": "Sold for",
  "counts.title": "Stock counts",
  "counts.description": "What the shelves say, against what the books say.",
  "counts.new": "New count",
  "counts.scope": "Count",
  "counts.scope.all": "Everything",
  "counts.scope.picked": "Only what I choose",
  "counts.counter": "Counted by",
  "counts.expected": "Books say",
  "counts.counted": "Shelf says",
  "counts.difference": "Difference",
  "counts.record": "Record the count",
  "counts.adjust": "Post the difference",
  "counts.adjustHint":
    "This moves stock with no delivery behind it, so it is recorded against you by name.",
  "document.number": "Number",
  "document.status": "Status",
  "document.date": "Raised",
  "document.lines": "Lines",
  "document.addLine": "Add a line",
  "document.quantity": "Quantity",
  "document.unitPrice": "Price each",
  "document.remarks": "Notes",
  "status.purchasing": "Buying",
  "status.head_office_reviewing": "With head office",
  "status.head_office_rejected": "Head office said no",
  "status.pending_inbound": "Waiting to be taken in",
  "status.ended": "Done",
  "status.voided": "Voided",
  "status.draft": "Draft",
  "status.waiting_review": "Waiting to be checked",
  "status.approving": "Being checked",
  "status.received": "Taken in",
  "status.rejected": "Rejected",
  "status.issued": "Gone out",
  "status.transferring": "In transit",
  "status.blocked": "Not enough stock",
  "status.counting": "Counting",
  "status.adjusted": "Posted",

  "nav.ordersSection": "Orders",
  "nav.orderList": "All orders",
  "nav.reports": "Reports",
  "nav.costs": "Store costs",
  "nav.deliveryProviders": "Delivery platforms",
  "nav.billing": "Subscription",
  "nav.campaigns": "Campaigns",
  "billing.title": "Your subscription",
  "billing.description": "The plan you are on, what it includes, and what you have paid.",
  "billing.plan": "Plan",
  "billing.perMonth": "{amount} a month",
  "billing.permanent": "This plan does not expire.",
  "billing.expires": "Runs until {date} — {days} days left.",
  "billing.lapsed": "Lapsed on {date}.",
  "billing.allowances": "What it allows",
  "billing.unmetered": "{used} — no limit",
  "billing.renew": "Extend",
  "billing.renewHint":
    "Extends the plan you are on. Changing plan needs a new contract — talk to us.",
  "billing.months": "Months",
  "billing.cycle": "Billed",
  "billing.monthly": "Monthly",
  "billing.yearly": "Yearly",
  "billing.payments": "Payments",
  "billing.noPayments": "Nothing has been billed yet.",
  "subscriptionStatus.normal": "Active",
  "subscriptionStatus.expired": "Expired",
  "subscriptionStatus.invalidated": "Suspended",
  "subscriptionStatus.cancelled": "Cancelled",
  "subscriptionStatus.trialing": "Trial",
  "quota.item": "Items",
  "quota.employee": "Staff",
  "quota.table": "Tables",
  "quota.customer": "Customers",
  "quota.supplier": "Suppliers",
  "quota.store": "Locations",
  "campaigns.title": "Campaigns",
  "campaigns.description": "Every Nth item off. Priced on the bill, not typed in by hand.",
  "campaigns.add": "Add a campaign",
  "campaigns.name": "Name",
  "campaigns.kind": "Offer",
  "campaigns.kind.half_price_second": "Half price on the second item",
  "campaigns.kind.buy_one_free_one": "Buy one, get one free",
  "campaigns.kind.nth_item": "Nth item discounted",
  "campaigns.nthItem": "Which item",
  "campaigns.discount": "How much off",
  "campaigns.discountHint": "A percentage. 100 means free.",
  "campaigns.consecutive": "Repeat through the basket",
  "campaigns.consecutiveHint": "On: four items give two at the offer. Off: it fires once per bill.",
  "campaigns.active": "Running",
  "campaigns.reads": "Every {nth} item, {percent}% off",
  "campaigns.deleteTitle": "Delete this campaign?",
  "campaigns.deleteBody":
    "The campaign is gone for good, not paused — switching it off stops the offer and keeps it for later. Bills already priced with it are unchanged.",
  "campaigns.record": "Record",
  "campaigns.label": "Label on the bill",
  "campaigns.items": "Items it covers",
  "campaigns.itemsAll": "The whole menu",
  "campaigns.window": "When it runs",
  "campaigns.validFrom": "Runs from",
  "campaigns.validTo": "Runs until",
  "nav.exports": "Exports",
  "nav.chainOrders": "Chain orders",
  "nav.insights": "Business insights",
  "nav.chainReports": "Chain reports",
  "orders.title": "Orders",
  "orders.description": "Every bill, what was on it, and what happened to it.",
  "orders.number": "Order",
  "orders.table": "Table",
  "orders.covers": "Covers",
  "orders.total": "Total",
  "orders.paid": "Paid",
  "orders.status": "Status",
  "orders.date": "Taken",
  "orders.diningStyle": "Service",
  "orders.employee": "Rung by",
  "orders.refund": "Refund",
  "orders.tip": "Tip",
  "orders.adjustTip": "Adjust tip",
  "orders.adjustTipTitle": "Adjust the tip on this tender",
  "orders.adjustTipHint":
    "The tip as it should now read, not the difference. Only until this trading day is counted.",
  "orders.tipAdjusted": "Tip adjusted.",
  "orders.refundTitle": "Refund this bill",
  "orders.refundAmount": "Amount",
  "orders.refundAmountHint": "Leave this empty to refund everything still taken.",
  "orders.refundReason": "Reason",
  "orders.refundable": "Still refundable",
  "orders.refunded": "Refunded.",
  "orders.reverse": "Reverse",
  "orders.reverseTitle": "Reverse this checkout",
  "orders.reverseWarning":
    "The bill goes back to being open, and any money taken is returned. Its table is occupied again.",
  "orders.reversed": "Checkout reversed.",
  "orders.items": "Items",
  "orders.taxLines": "Tax",
  "orders.payments": "Payment",
  "orders.history": "History",
  "orders.reprint": "Reprint the receipt",
  "orders.reprinted": "Queued for printing.",
  "orders.allStatuses": "Any status",
  "orders.allStyles": "Any service",
  "receipt.taxIncluded": "included",
  "orders.inclusiveNote": "Already included in the price",
  "payStatus.unpaid": "Open",
  "payStatus.partly_paid": "Part paid",
  "payStatus.paid": "Paid",
  "payStatus.credit": "On account",
  "payStatus.refunded": "Refunded",
  "payStatus.cancelled": "Cancelled",
  "orderItem.ordered": "Ordered",
  "orderItem.fired": "With the station",
  "orderItem.served": "Served",
  "orderItem.returned": "Returned",
  "orderItem.voided": "Voided",
  "event.created": "Order taken",
  "event.accepted": "Accepted",
  "event.item_added": "More ordered",
  "event.item_returned": "Item returned",
  "event.item_voided": "Item voided",
  "event.item_fired": "Sent to the station",
  "event.table_changed": "Table changed",
  "event.covers_changed": "Covers changed",
  "event.held": "Held",
  "event.resumed": "Resumed",
  "event.merged": "Merged",
  "event.split": "Split",
  "event.discounted": "Discounted",
  "event.tax_exempted": "Tax exempted",
  "event.tax_reinstated": "Tax reinstated",
  "event.tip_adjusted": "Tip adjusted",
  "event.paid": "Paid",
  "event.refunded": "Refunded",
  "event.reversed": "Check-out reversed",
  "event.cancelled": "Cancelled",
  "event.member_attached": "Member identified",
  "event.points_earned": "Points earned",
  "event.supplies_deducted": "Stock deducted",
  "creditStatus.open": "Open",
  "creditStatus.settled": "Settled",
  "creditStatus.written_off": "Written off",
  "insights.title": "Business insights",
  "insights.description": "How the chain is doing, and what looks wrong.",
  "insights.overview": "Overview",
  "insights.trend": "Trend",
  "insights.priceDistribution": "Price distribution",
  "insights.anomalies": "What looks wrong",
  "insights.rules": "Thresholds",
  "insights.storeCount": "Stores",
  "insights.allStores": "Every store",
  "insights.comparedWith": "against {from} to {to}",
  "insights.metric": "Metric",
  "insights.metric.net": "Net sales",
  "insights.metric.orders": "Orders",
  "insights.metric.covers": "Covers",
  "insights.metric.discount": "Discounts",
  "insights.metric.perCover": "Spend per cover",
  "insights.figure.net": "Net sales",
  "insights.figure.gross": "Gross sales",
  "insights.figure.discount": "Discounts",
  "insights.figure.refunded": "Refunds",
  "insights.figure.orders": "Orders",
  "insights.figure.covers": "Covers",
  "insights.column.store": "Store",
  "insights.column.item": "Item",
  "insights.column.lowest": "Cheapest at",
  "insights.column.highest": "Dearest at",
  "insights.column.spread": "Spread",
  "insights.column.median": "Median",
  "insights.column.stores": "Stores",
  "insights.column.perCover": "Per cover",
  "insights.column.rule": "Rule",
  "insights.priceScope":
    "Current menu prices at every location, whichever store is picked above. The date range does not apply, because there is no price history to read a past period against.",
  "insights.noSpread": "No item is priced differently between stores.",
  "insights.priceTruncated": "Showing {shown} of {total} items with the widest spread.",
  "insights.column.reading": "Reading",
  "insights.column.threshold": "Threshold",
  "insights.column.evidence": "Evidence",
  "insights.showEvidence": "Show me",
  "insights.rule.discountRate": "Discount rate",
  "insights.rule.voidRate": "Void rate",
  "insights.rule.refundRate": "Refund rate",
  "insights.rule.coverSpend": "Spend per cover",
  "insights.direction.above": "above",
  "insights.direction.below": "below the chain median",
  "insights.clear": "Nothing crossed a threshold in this period.",
  "insights.enabled": "Watched",
  "insights.saved": "Saved.",
  "chainOrders.title": "Chain orders",
  "chainOrders.description": "Every order taken across the chain.",
  "chainOrders.store": "Store",
  "chainOrders.record": "Record",
  "nav.members": "Members",
  "nav.memberList": "Customer list",
  "nav.memberBalance": "Store balance",
  "nav.loyalty": "Loyalty",
  "members.title": "Members",
  "members.description": "Who comes back, what they hold, and what they have earned.",
  "members.number": "Member no.",
  "members.name": "Name",
  "members.phone": "Phone",
  "members.balance": "Balance",
  "members.points": "Points",
  "members.tier": "Tier",
  "members.joined": "Joined",
  "members.enrol": "Enrol a member",
  "members.classification": "Member of",
  "members.anyClassification": "Anywhere in the chain",
  "members.classification.our_store": "This store",
  "members.classification.chain_store": "Another store",
  "members.classification.non_chain": "Outside the chain",
  "members.record": "Record",
  "members.registeredAt": "Registered at",
  "members.sharedWith": "Balance shared with",
  "members.noStoredValue": "Not on this plan",
  "members.topUp": "Top up",
  "members.adjust": "Correct the balance",
  "members.setPin": "Set the PIN",
  "members.pin": "PIN",
  "members.pinSet": "Set",
  "members.pinUnset": "Not set",
  "members.amount": "Amount",
  "members.topUpHint": "A matching tier's bonus is added automatically.",
  "members.adjustPoints": "Adjust points",
  "members.balanceHistory": "Balance history",
  "members.pointsHistory": "Points history",
  "members.noMovements": "No money has moved yet.",
  "members.noPoints": "No points yet.",
  "members.movement": "Movement",
  "members.balanceAfter": "Balance after",
  "members.whereStore": "Where",
  "members.movement.topup": "Top-up",
  "members.movement.spend": "Spent",
  "members.movement.refund": "Refunded",
  "members.movement.adjust": "Correction",
  "members.points.earn": "Earned",
  "members.points.redeem": "Redeemed",
  "members.points.adjust": "Correction",
  "members.points.expire": "Expired",
  "balance.title": "Store balance",
  "balance.description": "What you owe your members, and which store took it.",
  "balance.liability": "Owed to members",
  "balance.holders": "Members holding a balance",
  "balance.byStore": "By store",
  "balance.toppedUp": "Taken in",
  "balance.spent": "Served",
  "balance.held": "Still held",
  "balance.share": "Share",
  "loyalty.title": "Loyalty",
  "loyalty.description": "Top-up bonuses, and what a bill earns.",
  "loyalty.tiers": "Top-up tiers",
  "loyalty.noTiers": "No tiers yet — a top-up earns no bonus.",
  "loyalty.tierReads": "Pay {pay}, get {get}",
  "loyalty.pay": "Pays",
  "loyalty.bonus": "Bonus",
  "loyalty.points": "Points",
  "loyalty.pointsEnabled": "Award points on settled bills",
  "loyalty.perPoint": "Spend per point",
  "loyalty.pointWorth": "A point is worth",
  "loyalty.minimumRedeemable": "Fewest points redeemable",
  "action.yes": "Yes",
  "action.no": "No",
  "weekday.0": "Sun",
  "weekday.1": "Mon",
  "weekday.2": "Tue",
  "weekday.3": "Wed",
  "weekday.4": "Thu",
  "weekday.5": "Fri",
  "weekday.6": "Sat",
  "nav.marketing": "Marketing",
  "nav.coupons": "Coupons",
  "nav.promos": "Promos",
  "coupons.title": "Coupons",
  "coupons.description": "What you give away, to whom, and what it costs.",
  "coupons.name": "Coupon",
  "coupons.worth": "Worth",
  "coupons.minSpend": "Minimum spend",
  "coupons.issued": "Issued",
  "coupons.used": "Redeemed",
  "coupons.given": "Given away",
  "coupons.status": "Status",
  "coupons.live": "Live",
  "coupons.off": "Off",
  "coupons.design": "Design a coupon",
  "coupons.copy": "Copy",
  "coupons.send": "Send",
  "coupons.switchOn": "Switch on",
  "coupons.switchOff": "Switch off",
  "coupons.kind": "Kind",
  "coupons.kind.amount": "An amount off",
  "coupons.kind.percent": "A percentage off",
  "coupons.amountOff": "Amount off",
  "coupons.percentOff": "Percentage off, in basis points",
  "coupons.maxDiscount": "At most",
  "coupons.maxDiscountHint": "Leave empty for no ceiling.",
  "coupons.record": "Record",
  "coupons.limits": "How many go out",
  "coupons.issueLimit": "In total",
  "coupons.perMemberLimit": "Per member",
  "coupons.window": "When it runs",
  "coupons.validFrom": "Runs from",
  "coupons.validTo": "Runs until",
  "coupons.performance": "What it has done",
  "promos.title": "Promos",
  "promos.description": "Spend and save, in the order they are considered.",
  "promos.name": "Promo",
  "promos.offer": "Offer",
  "promos.when": "When",
  "promos.stacks": "Stacks",
  "promos.add": "Add a promo",
  "promos.reads": "Spend {threshold}, save {reduction}",
  "promos.threshold": "Spend at least",
  "promos.reduction": "Take off",
  "promos.days": "Days",
  "promos.everyDay": "Every day",
  "promos.deleteTitle": "Delete this promo?",
  "promos.deleteBody":
    "The promo is gone for good, not paused — switching it off stops the offer and keeps it for later. Bills already priced with it are unchanged.",
  "promos.record": "Record",
  "promos.order": "Considered at",
  "promos.hours": "Hours",
  "promos.allDay": "All day",
  "promos.validFrom": "Runs from",
  "promos.validTo": "Runs until",
  "pos.offline.ordering": "Taking orders",
  "pos.offline.payment": "Cash and offline payment",
  "pos.offline.printing": "Receipt printing",
  "pos.offline.noIncomingOrders": "Orders from other terminals will not reach this till",
  "pos.offline.noQrOrders": "QR orders placed while you are offline will not appear",
  "pos.offline.noOnlinePayment": "Online payment and member balance are unavailable",
  "pos.offline.noReports": "Reports and stock figures will be out of date",
  "pos.offline.willSync": "Everything taken offline will be sent now — it may take a moment",
  "pos.sync.syncing": "Data is synchronizing…",
  "pos.sync.waiting": "{count} waiting to send",
  "pos.sync.needsAttention": "{count} could not be sent",
  "pos.failures.title": "Could not be sent",
  "pos.failures.intro":
    "The server refused these and will keep refusing them. Each one needs a decision — most of all a payment, which is somebody's money.",
  "pos.failures.none": "Nothing is waiting on you.",
  "pos.failures.money": "Payment",
  "pos.failures.change": "Change",
  "pos.failures.queuedAt": "Taken {at}",
  "pos.failures.dismiss": "I have dealt with this",
  "pos.failures.close": "Close",

  "pos.bill.startTitle": "Start a bill",
  "pos.bill.startBody": "Choose a table, or start a take-away bill, before adding items.",
  "pos.bill.chooseTable": "Choose a table",
  "pos.bill.heldLabel": "{name} — on hold",
  "pos.bill.newTakeAway": "Take away",
  "pos.bill.itemsBlocked": "Start a bill before adding items",
  "pos.bill.open": "Open bills",
  "pos.bill.opening": "Opening\u2026",
  "pos.bill.notSent": "Not sent yet",
  "pos.bill.failed": "This bill could not be opened",
  "pos.bill.clearTable": "Clear table",
  "pos.bill.clearTableConfirm": "Release {table}? Nothing has been ordered on it.",
  "pos.bill.clearTableKeep": "Keep it",
  "pos.bill.discardBill": "Discard bill",
  "pos.bill.discardBillConfirm": "Discard this bill? Nothing has been ordered on it.",
  "pos.bill.discard": "Discard",
  "pos.bill.release": "Release",
  "pos.bill.occupied": "Occupied",
  "pos.bill.openThisBill": "Open this bill",
  "pos.bill.resumeOffline": "This bill was opened on another terminal. Go online to open it here.",
  "pos.cart.table": "Table",
  "pos.cart.takeAway": "Take away",
  "pos.cart.covers": "{count} covers",
  "pos.cart.editCovers": "Hold to change the covers",
  "pos.cart.empty": "Nothing on this bill yet.",
  "pos.cart.ordered": "Ordered ({count})",
  "pos.cart.latestOrder": "Latest order ({count})",
  "pos.cart.subtotal": "Subtotal",
  "pos.cart.discount": "Discount",
  "pos.cart.addDiscount": "Add",
  "pos.cart.service": "Service",
  "pos.cart.total": "Total",
  "pos.cart.taxFree": "Tax free",
  "pos.cart.taxFreeApply": "Apply",
  "pos.cart.taxFreeRemove": "Remove",
  "pos.cart.hold": "Hold",
  "pos.cart.resume": "Resume",
  "pos.cart.sendToStation": "Send to station",
  "pos.cart.settle": "Settle",
  "pos.sync.busy": "Syncing…",
  "pos.sync.failed": "{count} failed",
  "pos.sync.queued": "{count} queued",
  "pos.menu.all": "All",
  "pos.menu.loading": "Loading the menu…",
  "pos.menu.soldOut": "Sold out",
  "pos.menu.searchPlaceholder": "Search by name or code",
  "pos.menu.result": "{count} result",
  "pos.menu.results": "{count} results",
  "pos.menu.inCart": "{count} in the cart",
  "pos.item.quantity": "Quantity",
  "pos.item.note": "Note for the station",
  "pos.item.notePlaceholder": "No ice, extra spicy…",
  "pos.item.chooseOne": "choose one",
  "pos.item.chooseAny": "choose at least one",
  "pos.item.optional": "optional",
  "pos.item.cancel": "Cancel",
  "pos.item.choose": "Choose {group}",
  "pos.item.add": "Add {total}",
  "pos.mode.online": "Online",
  "pos.mode.offline": "Offline",
  "pos.mode.goOffline": "Go offline?",
  "pos.mode.comeOnline": "Come back online?",
  "pos.mode.stillAble": "This till will still be able to:",
  "pos.mode.butNot": "But not:",
  "pos.mode.allSent": "Everything is already sent.",
  "pos.mode.stayOnline": "Stay online",
  "pos.mode.stayOffline": "Stay offline",
  "pos.mode.switchToOnline": "Go online",
  "pos.mode.switchToOffline": "Go offline",
  "pos.identify.heading": "Scan to sign in",
  "pos.identify.instruction":
    "Point your phone's camera at this code, then confirm in the staff app.",
  "pos.identify.codeLabel": "Or type this code:",
  "pos.identify.denied": "That sign-in was declined. Scan again.",
  "pos.identify.notConfigured":
    "This till has no staff app address set, so nobody can sign in here. Ask whoever set the till up.",
  "pos.identify.unavailable":
    "This till cannot show a sign-in code right now. Check the connection.",
  "pos.identify.retry": "Try again",
  "pos.identify.choice.checkIn": "Check in",
  "pos.identify.choice.take": "Take till",
  "pos.identify.choice.checkInAndTake": "Check in and take till",
  "pos.identify.choice.checkOut": "Check out",
  "pos.identify.choice.cancel": "Cancel",
  "pos.identify.choice.jobHeading": "Which job?",
  "pos.identify.choice.noJobs": "No jobs are set up for this store yet.",
  "pos.identify.choice.elsewhere": "Already clocked in at another store.",
  "pos.identify.choice.checkedIn": "Checked in.",
  "pos.identify.choice.checkedOut": "Checked out.",
  "pos.shift.title": "The drawer",
  "pos.shift.failed": "That did not work. Please try again.",
  "pos.shift.handOver": "Hand over",
  "pos.shift.noneOpen": "No shift is open. Count the float and open one to start taking money.",
  "pos.shift.openingFloat": "Opening float",
  "pos.shift.open": "Open the shift",
  "pos.shift.openNow": "Open",
  "pos.shift.number": "Shift {number}",
  "pos.shift.openedBy": "Opened by",
  "pos.shift.bills": "Bills settled",
  "pos.shift.byMethod": "Taken, by method",
  "pos.shift.nothingYet": "Nothing taken yet.",
  "pos.shift.expectedInDrawer": "Expected in the drawer",
  "pos.shift.railCounts": "What each rail says",
  "pos.shift.railCountsHint": "Check the bank app or the terminal. Leave one blank if you did not.",
  "pos.shift.cardNotInDrawer":
    "Card and online takings are not in the drawer, so they are not counted here.",
  "pos.shift.counted": "Counted",
  "pos.shift.balances": "It balances.",
  "pos.shift.short": "{amount} short",
  "pos.shift.over": "{amount} over",
  "pos.shift.close": "Close the shift",
  "pos.settle.title": "Settle",
  "pos.settle.total": "Total",
  "pos.settle.stillToPay": "Still to pay",
  "pos.settle.change": "Change",
  "pos.settle.tip": "Tip",
  "pos.settle.noTip": "No tip",
  "pos.settle.tipOther": "Another amount",
  "pos.settle.due": "To pay",
  "pos.station.title": "Pass",
  "pos.station.backToTill": "Till",
  "pos.station.all": "Every station",
  "pos.station.empty": "Nothing to make.",
  "pos.station.waiting": "{minutes} min",
  "pos.station.course": "Course {number}",
  "pos.settle.amountTendered": "Amount tendered",
  "pos.settle.back": "Back",
  "pos.settle.done": "Done",
  "pos.billActions.title": "Bill actions",
  "pos.billActions.merge": "Merge",
  "pos.billActions.mergeHint": "Fold another open bill into this one. It closes.",
  "pos.billActions.noOthers": "No other bill is open.",
  "pos.billActions.split": "Split",
  "pos.billActions.splitHint": "Move lines onto a bill of their own.",
  "pos.billActions.splitDo": "Move to a new bill",
  "pos.billActions.noFired": "Only lines a station already has can be moved.",
  "pos.billActions.needsConnection": "Merging and splitting both need a connection.",
  "pos.billActions.back": "Back",
  "pos.line.title": "Amend line",
  "pos.line.void": "Void",
  "pos.line.return": "Return",
  "pos.line.back": "Back",
  "pos.discount.title": "Discount",
  "pos.discount.amount": "Discount amount",
  "pos.discount.tooLarge": "A discount cannot exceed the bill.",
  "pos.discount.back": "Back",
  "pos.discount.clear": "Clear",
  "pos.discount.apply": "Apply",
  "pos.table.title": "Table",
  "pos.table.seats": "{count} seats",
  "pos.table.howMany": "{table} — how many?",
  "pos.table.status.free": "Free",
  "pos.table.status.seated": "Seated",
  "pos.table.status.ordered": "Ordered",
  "pos.table.status.billRequested": "Bill requested",
  "pos.signIn.title": "Set this till up",
  "pos.signIn.subtitle": "Approved from the back office. Staff sign in by scanning afterwards.",
  "pos.signIn.expiredTitle": "Sign this till back in",
  "pos.signIn.expiredSubtitle":
    "This terminal's session has ended. Nothing has been lost — anything still waiting will send once you are back in.",
  "pos.signIn.codeLabel": "Pairing code",
  "pos.signIn.instruction": "Ask a manager to open {uri} and enter this code, or scan it.",
  "pos.signIn.waiting": "Waiting for approval…",
  "pos.signIn.retrying": "Retrying — checking the connection…",
  "pos.signIn.denied": "The pairing request was refused.",
  "pos.signIn.failed": "Something went wrong.",
  "pos.signIn.newCode": "Show a new code",

  "staff.signIn.title": "Sign in",
  "staff.signIn.subtitle": "Use your passkey, or your username and password.",
  "staff.signIn.passkey": "Sign in with a passkey",
  "staff.signIn.or": "or",
  "staff.signIn.username": "Username",
  "staff.signIn.password": "Password",
  "staff.signIn.connect": "Sign in",
  "staff.signIn.rejected": "Those details did not work.",
  "staff.signIn.passkeyRejected": "That passkey did not work.",
  "staff.signIn.unreachable": "Could not reach the server.",
  "staff.home.greeting": "Signed in as",
  "staff.home.signOut": "Sign out",
  "staff.home.scan": "Scan a till code",
  "staff.home.scanRefused": "That doesn't look like a till code. Enter it below instead.",
  "staff.home.enterCode": "Enter a till code",
  "staff.home.enterCodePlaceholder": "e.g. ABCD-1234",
  "staff.home.enterCodeSubmit": "Continue",
  "staff.home.passkeyOffer": "Sign in faster next time with a passkey on this phone.",
  "staff.home.passkeyAdd": "Add a passkey",
  "staff.home.passkeyAdded": "Passkey added. Next time, choose Sign in with a passkey.",
  "staff.home.passkeyStale": "For your security, sign out and sign in again, then add the passkey.",
  "staff.home.passkeyFailed": "Could not add a passkey on this device.",

  "staff.approve.continueAfterSignIn": "Signing in will continue to the till's request.",
  "staff.approve.checking": "Checking the code…",
  "staff.approve.instruction": "Check this matches the code shown on the till, then confirm.",
  "staff.approve.take": "It's me",
  "staff.approve.notMe": "Not me",
  "staff.approve.approved": "Carry on at the till.",
  "staff.approve.backHome": "Back home",
  "staff.approve.notForTill": "This code pairs a till rather than naming who is standing at it.",
  "staff.approve.taken": "Someone else is already approving this code.",
  "staff.approve.used": "This code has already been used.",
  "staff.approve.gone": "This code is no longer valid.",
  "staff.approve.refused": "This account cannot take a till.",
  "staff.approve.retry": "Try again",

  "staff.approve.someoneElse": "Someone else",
  "staff.approve.pickEmployee": "Put someone on this till",
  "staff.approve.search": "Search",
  "staff.approve.confirmFor": "Put {name} on this till?",
  "staff.approve.assigned": "{name} will be signed in at the till in a few seconds.",
  "staff.approve.assignRefused": "Could not put them on the till.",

  "diner.title": "Order",
  "diner.scanPrompt": "Scan the code on your table to see the menu.",
  "diner.takeaway": "Takeaway",
  "diner.closed": "Ordering is closed right now.",
  "diner.all": "All",
  "diner.soldOut": "Sold out",
  "voice.newOrder": "New order received.",
  "diner.sold": "{count} sold",
  "diner.sending": "Sending…",
  "diner.order": "Order {count} · {total}",
  "diner.sendFailed": "Could not send that.",
  "diner.locating": "Checking where you are…",
  "diner.location.denied":
    "This restaurant needs your location to take an order. Allow location access and try again.",
  "diner.location.unavailable":
    "We could not find your location. Check that location is on, then try again.",
  "diner.chooseOne": "· choose one",
  "diner.chooseRange": "· choose {min}–{max}",
  "diner.chooseUpTo": "· up to {max}",
  "diner.extras": "Extras",
  "diner.noteLabel": "Anything we should know?",
  "diner.notePlaceholder": "No chilli, please",
  "diner.chooseGroup": "Choose {group}",
  "diner.add": "Add · {total}",
  "diner.yourNumber": "Your number",
  "diner.status.pending": "Sent to be made",
  "diner.status.accepted": "Accepted",
  "diner.status.preparing": "Being made",
  "diner.status.served": "Served",
  "diner.discount": "Discount",
  "diner.service": "Service",
  "diner.total": "Total",
  "diner.paid": "Paid, thank you.",
  "diner.payFirst": "Please pay to confirm your order.",
  "diner.payAtCounter": "Pay at the counter when you are ready.",
  "diner.couponCode": "Coupon code",
  "diner.couponFailed": "That code did not work.",
  "diner.member.signIn": "Sign in",
  "diner.member.signInTitle": "Your membership",
  "diner.member.signInExplainer":
    "Sign in with your phone number to see your points, balance and coupons, and to earn on this bill.",
  "diner.member.phone": "Phone number",
  "diner.member.sendCode": "Send me a code",
  "diner.member.codeSent": "We sent a code to {phone}.",
  "diner.member.devCode":
    "No SMS provider is configured on this server, so the code is shown here: {code}",
  "diner.member.code": "Code",
  "diner.member.nameOptional": "Your name (only if you are new here)",
  "diner.member.differentNumber": "Use a different number",
  "diner.member.failed": "That did not work. Please try again.",
  "diner.member.title": "Your membership",
  "diner.member.number": "Member {number}",
  "diner.member.points": "Points",
  "diner.member.points.short": "{points} pts",
  "diner.member.pointsWorth": "Worth {amount} here",
  "diner.member.noPointsScheme": "This restaurant does not run a points scheme.",
  "diner.member.pointsHistory": "Recent points",
  "diner.member.balance": "Balance",
  "diner.member.held": "{amount} is being held against an open bill",
  "diner.member.pinNeeded": "You will be asked for your PIN when you pay with this.",
  "diner.member.topUp": "Top up",
  "diner.member.topUpAtCounter": "Ask at the counter to top up — it cannot be done from here yet.",
  "diner.member.getsYou": "gets you {amount}",
  "diner.member.coupons": "Coupons",
  "diner.member.noCoupons": "You have no coupons yet.",
  "diner.member.minSpend": "Spend at least {amount}",
  "diner.member.coupon.issued": "Ready",
  "diner.member.coupon.used": "Used",
  "diner.member.coupon.expired": "Expired",
  "diner.member.coupon.void": "Void",
  "diner.member.earnOnThisBill": "Sign in to earn points on this bill.",
  "diner.member.addToThisBill": "Add {name} to this bill to earn points.",
  "diner.member.claim": "Add me to this bill",
  "diner.member.onThisBill": "{name} is on this bill.",
  "diner.apply": "Apply",
  "diner.orderMore": "Order more",
  "diner.cancelled": "This order was cancelled",
  "reports.title": "Reports",
  "reports.description": "The numbers behind the service, on your own trading day.",
  "reports.pick": "Report",
  "reports.from": "From",
  "reports.to": "To",
  "reports.consolidate": "Merge the period into one row",
  "reports.groupBy": "Group by",
  "reports.columns": "Columns",
  "reports.export": "Export",
  "reports.exported": "Queued. It will appear in Exports.",
  "reports.total": "Total",
  "reports.empty": "Nothing was taken in this period.",
  "reports.master": "Master report",
  "reports.itemCategory": "Items and categories",
  "reports.paymentMethods": "Payment methods",
  "reports.hourlySales": "Sales by hour",
  "reports.discounts": "Discounts",
  "reports.voids": "Voids",
  "reports.topSelling": "Top selling",
  "reports.daily": "Daily",
  "reports.tables": "Tables",
  "reports.shifts": "Shifts",
  "reports.credit": "Tabs",
  "reports.stock": "Stock movement",
  "reports.column.initialStock": "Opening stock",
  "reports.column.inbound": "In",
  "reports.column.outbound": "Out",
  "reports.column.endingStock": "Closing stock",
  "reports.column.group": "Breakdown",
  "reports.column.store": "Store",
  "reports.column.orders": "Orders",
  "reports.column.covers": "Covers",
  "reports.column.grossSales": "Gross sales",
  "reports.column.discount": "Discount",
  "reports.column.campaignDiscount": "Campaign discount",
  "reports.column.netExTax": "Net excl. tax",
  "reports.column.netSales": "Net sales",
  "reports.column.tax": "Tax",
  "reports.column.serviceCharge": "Service charge",
  "reports.column.netIncTax": "Net incl. tax",
  "reports.column.payments": "Taken",
  "reports.column.refunds": "Refunded",
  "reports.column.averageOrder": "Average bill",
  "reports.column.averageCover": "Average per cover",
  "reports.column.quantity": "Quantity",
  "reports.column.method": "Method",
  "reports.column.tenders": "Tenders",
  "reports.column.taken": "Taken",
  "reports.column.net": "Net",
  "reports.column.hour": "Hour",
  "reports.column.orderNo": "Order",
  "reports.column.date": "Date",
  "reports.column.who": "Who",
  "reports.column.what": "What",
  "reports.column.detail": "Detail",
  "reports.column.reason": "Reason",
  "reports.column.item": "Item",
  "reports.column.rankByQuantity": "Rank by quantity",
  "reports.column.rankByRevenue": "Rank by revenue",
  "reports.column.table": "Table",
  "reports.column.turnTime": "Minutes per sitting",
  "reports.column.shift": "Shift",
  "reports.column.openedBy": "Opened by",
  "reports.column.closedBy": "Closed by",
  "reports.column.float": "Float",
  "reports.column.expectedCash": "Drawer should hold",
  "reports.column.countedCash": "Counted",
  "reports.column.difference": "Difference",
  "reports.column.customer": "Customer",
  "reports.column.amount": "Amount",
  "reports.column.settled": "Settled",
  "reports.column.outstanding": "Outstanding",
  "reports.column.ageDays": "Days open",
  "reports.column.status": "Status",
  "reports.hourlySalesDetails": "Sales by hour and item",
  "reports.newItems": "New dishes",
  "reports.coupons": "Coupons",
  "reports.discountPromos": "Promo campaigns",
  "reports.sales": "Sales by staff",
  "reports.items": "Items and wastage",
  "reports.storeCosts": "Store costs",
  "reports.itemCosts": "Item costs",
  "reports.profit": "Profit",
  "reports.tipReporting": "Tip reporting (8027)",
  "reports.tipsByOccupation": "Tips by occupation",
  "reports.labourHours": "Hours worked",
  "reports.column.grossReceipts": "Gross receipts",
  "reports.column.chargedTips": "Charged tips",
  "reports.column.chargeReceipts": "Charge receipts with a tip",
  "reports.column.cashTips": "Cash tips",
  "reports.column.directlyTipped": "Paid to directly tipped",
  "reports.column.indirectlyTipped": "Paid to indirectly tipped",
  "reports.column.eightPercent": "8% of gross receipts",
  "reports.column.job": "Job",
  "reports.column.occupationCode": "Occupation code",
  "reports.column.tipStatus": "Tip status",
  "reports.column.businessDate": "Trading day",
  "reports.column.openEntries": "Not clocked out",
  "reports.column.minutes": "Minutes",
  "reports.unattributed": "No staff member",
  "reports.column.averageAmount": "Average amount",
  "reports.column.addedOn": "Added",
  "reports.column.labels": "Labels",
  "reports.column.quantityShare": "Share of quantity",
  "reports.column.amountShare": "Share of sales",
  "reports.column.coupon": "Coupon",
  "reports.column.issued": "Issued",
  "reports.column.redeemed": "Redeemed",
  "reports.column.expired": "Expired",
  "reports.column.redemptionRate": "Redemption rate",
  "reports.column.orderAmount": "Bill total",
  "reports.column.discountShare": "Discount as share of bills",
  "reports.column.promo": "Campaign",
  "reports.column.returnedQuantity": "Returned",
  "reports.column.voidedQuantity": "Voided",
  "reports.column.wastageRate": "Wastage rate",
  "reports.column.received": "Received",
  "reports.column.supplyCost": "Supply costs",
  "reports.column.marketingCost": "Marketing",
  "reports.column.laborCost": "Wages",
  "reports.column.administrativeCost": "Overheads",
  "reports.column.adHocCost": "One-off",
  "reports.column.totalCosts": "Total costs",
  "reports.column.profit": "Profit",
  "reports.column.grossMargin": "Margin",
  "reports.column.openingCash": "Opening cash",
  "reports.column.salesQty": "Quantity sold",
  "reports.column.averageCost": "Average cost",
  "reports.column.originalPaid": "Before discount",
  "reports.column.actualPaid": "After discount",
  "reports.column.marginBeforeDiscount": "Margin before discount",
  "reports.column.marginAfterDiscount": "Margin after discount",
  "reports.column.creditTaken": "Put on a tab",
  "reports.column.expense": "Paid out",
  "reports.column.profitBalance": "Profit balance",
  "costs.title": "Store costs",
  "costs.description": "What the business spends, alongside what it takes.",
  "costs.add": "Add a cost",
  "costs.edit": "Edit a cost",
  "costs.date": "Business day",
  "costs.type": "What for",
  "costs.amount": "Amount",
  "costs.note": "Note",
  "costs.category": "Group",
  "costs.enteredBy": "Entered by",
  "costs.empty": "Nothing has been entered for this period.",
  "costs.deleteTitle": "Remove this cost entry?",
  "costs.deleteBody":
    "The store costs report stops counting it. What the store took that day is unchanged.",
  "costs.record": "Record",
  "costs.category.marketing": "Marketing",
  "costs.category.labor": "Labour",
  "costs.category.administrative": "Overheads",
  "costs.category.ad_hoc": "One-off",
  "costs.type.platformFee": "Platform fee",
  "costs.type.advertising": "Advertising",
  "costs.type.marketingDiscounts": "Promotional discounts",
  "costs.type.deliveryFee": "Shipping and delivery",
  "costs.type.serviceFee": "Service fee",
  "costs.type.salary": "Salary",
  "costs.type.salesCommission": "Sales commission",
  "costs.type.overtime": "Overtime",
  "costs.type.insurance": "Insurance",
  "costs.type.benefitsTraining": "Benefits and training",
  "costs.type.accommodation": "Accommodation allowance",
  "costs.type.transportation": "Transport allowance",
  "costs.type.travel": "Travel allowance",
  "costs.type.rent": "Rent",
  "costs.type.propertyFee": "Building charges",
  "costs.type.water": "Water",
  "costs.type.electricity": "Electricity",
  "costs.type.gas": "Gas",
  "costs.type.renovation": "Fit-out, written down",
  "costs.type.equipmentPurchase": "Equipment bought",
  "costs.type.equipmentRepair": "Equipment repairs",
  "costs.type.depreciation": "Equipment depreciation",
  "costs.type.adHoc": "One-off expense",
  "exports.title": "Exports",
  "exports.description": "Files you asked for, and whether they are ready.",
  "exports.report": "Report",
  "exports.period": "Period",
  "exports.rows": "Rows",
  "exports.requestedBy": "Asked for by",
  "exports.requested": "Asked for",
  "exports.download": "Download",
  "exports.expiresOn": "Available until {date}",
  "exports.expired": "No longer available — ask for it again.",
  "exports.record": "Export",
  "exports.completed": "Finished",
  "exports.availability": "Availability",
  "exports.failure": "Why it failed",
  "exportStatus.queued": "Waiting",
  "exportStatus.running": "Running",
  "exportStatus.ready": "Ready",
  "exportStatus.failed": "Failed",

  "dashboard.title": "Today",
  "dashboard.description": "How the day is going, against the same day last week.",
  "dashboard.updated": "Updated",
  "dashboard.refresh": "Update now",
  "dashboard.vsLastWeek": "vs. same day last week",
  "dashboard.byMethod": "By payment method",
  "dashboard.ranking": "Selling most",
  "dashboard.daily": "By day",
  "dashboard.money.totalSales": "Total sales",
  "dashboard.money.received": "Received",
  "dashboard.money.credit": "On account",
  "dashboard.money.unpaid": "Still owed",
  "dashboard.money.discount": "Discounts",
  "dashboard.money.refund": "Refunded",
  "dashboard.money.cancelled": "Cancelled",
  "dashboard.counts.orders": "Orders",
  "dashboard.counts.receivedOrders": "Paid",
  "dashboard.counts.creditOrders": "On account",
  "dashboard.counts.unpaidOrders": "Open",
  "dashboard.counts.discountOrders": "Discounted",
  "dashboard.counts.refundOrders": "Refunded",
  "dashboard.counts.cancelledOrders": "Cancelled",

  "auth.signIn": "Sign in",
  "auth.signOut": "Sign out",
  "auth.email": "Email",
  "auth.password": "Password",
  "auth.signingIn": "Signing in…",
  "auth.invalidCredentials": "That email and password do not match an account.",
  "auth.title": "Sign in to TOVI",
  "auth.subtitle": "Back office",
  "auth.showPassword": "Show password",
  "auth.operatorTitle": "Sign in to TOVI Operator",
  "auth.operatorSubtitle": "Platform staff only",
  "auth.operatorAccountTitle": "This account belongs to the operator console.",
  "auth.operatorAccountBody": "Sign in with a merchant account to use the back office.",
  "auth.merchantAccountTitle": "This account belongs to the back office.",
  "auth.merchantAccountBody": "Sign in with an operator account to use this console.",
  "auth.accountUnusableTitle": "This account cannot use this console.",
  "auth.accountUnusableBody":
    "It may be disabled, or not finished being set up. Ask whoever manages it to check.",

  "shell.store": "Store",
  "shell.role": "Role",
  "shell.language": "Language",
  "shell.loading": "Loading…",
  "shell.openNav": "Open navigation",
  "shell.closeNav": "Close navigation",
  "shell.notifications": "Notifications",
  "shell.account": "Account menu",
  "shell.venue": "Current venue",
  "shell.settings": "Settings",
  "shell.emptyTitle": "Nothing here yet",
  "shell.emptyBody":
    "This is the authenticated shell. Menu, orders and reports arrive in later phases.",

  "error.generic": "Something went wrong.",
  "error.notFound": "Not found.",
  "error.forbidden": "You do not have permission to do that.",
  "error.notEntitled": "That is not part of your plan.",
  "error.quotaExceeded": "You have reached your limit.",

  /**
   * A refusal a merchant or a cashier actually meets, keyed by the `reason` the
   * API sends beside the code (`packages/shared/src/http/error-reasons.ts`).
   * The four keys above are the generic sentences; these are the specific ones,
   * and `resolveErrorMessage` prefers them over the server's English.
   */
  "error.inventoryForbidden": "You do not have inventory access to that.",
  "error.capabilityNotDelegated": "Your head office has not enabled that for this store.",
  "error.rateLimited": "Too many attempts — try again shortly.",
  "error.signInRequired": "Sign in to continue.",
  "error.accountDisabled": "This account is disabled.",
  "error.accountNoStore": "This account is not attached to a store.",
  "error.storeNotAllowed": "That store is not one you can work in.",
  "error.pinRequired": "Sign in at this till to continue.",
  "error.pinSessionExpired": "Your sign-in at this till has expired. Scan again.",
  "error.pinMismatch": "That PIN does not match.",
  "error.terminalRequired": "Only a terminal may identify a cashier.",
  "error.grantNotRecognised": "That sign-in has expired. Scan the code again.",
  "error.deviceCodeNotPending": "That code cannot be approved on someone's behalf.",
  "error.alreadyClockedIn": "Already clocked in.",
  "error.notClockedIn": "Not clocked in.",
  "error.clockedInElsewhere": "Clocked in at another store, not here.",
  "error.unitInUse": "Supplies are stocked in this unit.",
  "error.unitSeeded": "A built-in unit cannot be deleted, only hidden by sorting.",
  "error.unitSelfConversion": "A unit converts to itself one for one.",
  "error.unitDimensionMismatch": "Those two units measure different things and do not convert.",
  "error.supplyCategoryHasChildren": "Empty the sub-categories first.",
  "error.supplyCategoryInUse": "Supplies are filed under this category.",
  "error.supplyCategorySelfParent": "A category cannot contain itself.",
  "error.supplyCategoryTooDeep": "Categories go two levels deep, no further.",
  "error.brandInUse": "Supplies carry this brand.",
  "error.supplyHasStock": "There is stock of this supply. Write it off first.",
  "error.supplyNumberTaken": "That number is already in use.",
  "error.barcodeTaken": "That barcode already belongs to a supply.",
  "error.menuCategoryInUse": "Move or delete this category's items first.",
  "error.itemNumberTaken": "Another item already uses that code.",
  "error.tableAreaInUse": "Move or delete this area's tables first.",
  "error.stationHasPrinters": "Move this station's printers elsewhere first.",
  "error.stationHasItems": "Route this station's items elsewhere first.",
  "error.printerHasItems": "Some items print only to this printer. Repoint them first.",
  "error.printerNeedsAddress": "Give this printer an address before pairing it.",
  "error.printerNeedsStation": "A station printer needs a station for items to route to.",
  "error.paymentMethodBuiltIn":
    "That payment method is built in and cannot be deleted. Turn it off instead.",
  "error.tipPoolPaidOut": "That pool has paid out. Switch it off rather than removing it.",
  "error.tableAlreadyHasBill": "This table already has a bill open.",
  "error.billAlreadySettled": "This bill has already been settled.",
  "error.billNotCheckedOut": "This bill has not been checked out.",
  "error.orderCancelled": "This order was cancelled.",
  "error.settledBillNeedsReverse": "Reverse the check-out before changing a settled bill.",
  "error.moneyTakenUseRefund": "Money has been taken; refund or reverse it instead.",
  "error.nothingTakenYet": "Nothing has been taken yet.",
  "error.nothingLeftToRefund": "There is nothing left to refund.",
  "error.refundExceedsTaken": "That is more than was taken.",
  "error.paymentExceedsOutstanding": "That is more than the bill outstanding.",
  "error.paymentMethodUnavailable": "That payment method is not available here.",
  "error.paymentProviderNotInstalled":
    "That payment method names a provider that is not installed.",
  "error.paymentRailPending": "That payment has not finished; try again shortly.",
  "error.paymentAsyncUnsupported":
    "That payment method settles after the payer acts, which this till cannot wait for yet.",
  "error.discountExceedsBill": "A discount cannot exceed the bill.",
  "error.returnExceedsOrdered": "More was returned than was ordered.",
  "error.takingsCounted":
    "That day's takings have been counted. A tip can only be changed before the day ends.",
  "error.tipNotSupportedOnTender": "That tender is not one a tip can be added to.",
  "error.mergeIntoSelf": "An order cannot be merged into itself.",
  "error.mergeSourcePaid": "That bill has already taken money.",
  "error.splitNoSeats": "No line on this bill is attributed to a seat.",
  "error.splitSingleSeat": "Every line is on one seat, so there is nothing to split.",
  "error.splitLeavesEmptyBill": "Splitting off every line would leave an empty bill.",
  "error.courseNothingWaiting": "Nothing on that course is waiting to be made.",
  "error.giftCardCodeRequired": "Which gift card? A code is needed to redeem one.",
  "error.giftCardVoided": "That card has been voided.",
  "error.giftCardExpired": "That card has expired.",
  "error.giftCardCurrencyMismatch": "That card is in a different currency.",
  "error.giftCardInsufficient": "That card does not hold that much.",
  "error.memberRequired": "Say who the member is before spending their balance.",
  "error.memberBlocked": "That member is blocked.",
  "error.memberBalanceInsufficient": "That is more than the member's balance.",
  "error.memberPointsInsufficient": "That is more points than the member has.",
  "error.tabClosed": "That tab is already closed.",
  "error.tabNothingLeft": "There is nothing left on this tab.",
  "error.tabRepayExceeds": "That is more than the tab owes.",
  "error.tabRepaidWithTab": "A tab cannot be repaid with another tab.",
  "error.couponSwitchedOff": "That coupon is switched off.",
  "error.couponUsed": "That coupon has already been used.",
  "error.couponExpired": "That coupon has expired.",
  "error.couponNotYetValid": "That coupon is not valid yet.",
  "error.couponOtherMember": "That coupon belongs to a member who is not on this bill.",
  "error.couponBillEmpty": "There is nothing on this bill yet.",
  "error.couponBelowMinimum": "This bill does not reach the coupon's minimum.",
  "error.orderLocationRequired": "This restaurant needs your location to take an order.",
  "error.orderTooFar": "You are too far from the restaurant to order.",
  "error.shiftAlreadyOpen": "A shift is already open. Close it before opening another.",
  "error.shiftAlreadyClosed": "That shift is already closed.",
  "error.noShiftOpen": "No shift is open.",
  "error.shiftTenderNothingTaken": "This shift took nothing on one of those methods.",
  "error.lastRoleEditor": "This is the last role that can edit roles.",
  "error.photoNotJpeg": "That is not a JPEG image.",
  "error.photoTooLarge": "That photo is too large.",
  "error.passwordForSelf": "You cannot set your own password here.",
  "error.employeeOutranksYou":
    "This person can do things your role cannot, so you cannot set their password.",
  "error.roleOutranksYou": "You cannot give someone a role that can do more than yours.",

  /**
   * The operator console.
   *
   * Its own namespace because the console is TOVI's own staff rather than a
   * merchant's, and none of its copy is a merchant's copy — a "merchant" is the
   * thing being administered here and the reader everywhere else. What it does
   * share it takes from above rather than restating: `action.*`, `list.*`,
   * `auth.*`, `shell.*` and `quota.*` are the same words in both consoles.
   */
  "operator.shell.console": "Operator console",
  "operator.shell.noRole": "No role",

  "operator.nav.overview": "Overview",
  "operator.nav.merchants": "Merchants",
  "operator.nav.catalog": "Catalog",
  "operator.nav.finance": "Finance",
  "operator.nav.platform": "Platform",

  /* Column headings more than one table needs. A heading each table restated
     would be the same word translated four ways by four different reviewers. */
  "operator.field.merchant": "Merchant",
  "operator.field.merchants": "Merchants",
  "operator.field.plan": "Plan",
  "operator.field.status": "Status",
  "operator.field.country": "Country",
  "operator.field.partner": "Partner",
  "operator.field.code": "Code",
  "operator.field.scope": "Scope",
  "operator.field.price": "Price",
  "operator.field.tier": "Tier",
  "operator.field.expires": "Expires",
  "operator.field.stores": "Stores",
  "operator.field.transactions": "Transactions",
  "operator.field.permanent": "Permanent",
  "operator.field.perMonth": "{price}/mo",
  "operator.field.monthly": "Monthly",
  "operator.field.yearly": "Yearly",

  "operator.dashboard.title": "Dashboard",
  "operator.dashboard.period": "{start} to {end}",
  "operator.dashboard.loadFailed": "Could not load the dashboard.",
  "operator.dashboard.new": "New merchants",
  "operator.dashboard.newHint": "Added this period",
  "operator.dashboard.cumulative": "Cumulative",
  "operator.dashboard.cumulativeHint": "Total on the list",
  "operator.dashboard.active": "Active",
  "operator.dashboard.activeHint": "Transaction amount above zero",
  "operator.dashboard.activeRate": "Active rate",
  "operator.dashboard.noPreviousPeriod": "No previous period",
  "operator.dashboard.monthOnMonth": "{value} month on month",
  "operator.dashboard.revenue": "Subscription revenue",
  "operator.dashboard.transactionsHint": "Subscription payments",
  "operator.dashboard.transactionAmount": "Transaction amount",
  "operator.dashboard.pendingSettlement": "Pending settlement",
  "operator.dashboard.settled": "Settled",
  "operator.dashboard.allTime": "All time",
  "operator.dashboard.cumulativeRevenue": "Total cumulative revenue",
  "operator.dashboard.pendingPlusSettled": "Pending plus settled",

  "operator.merchants.title": "Merchants",
  "operator.merchants.description":
    "Every merchant on the platform, with their current plan and subscription state.",
  "operator.merchants.empty": "No merchants match this search.",
  "operator.merchants.search": "Search merchants",
  "operator.merchants.subscription": "Subscription",
  "operator.merchants.notProvisioned": "Not provisioned",
  "operator.merchants.anySubscription": "Any subscription",
  "operator.merchants.anyStatus": "Any status",
  "operator.merchants.new": "New merchant",
  "operator.merchants.loadFailed": "Could not load this merchant.",
  "operator.merchants.created": "{country} · created {date}",
  "operator.merchants.signContract": "Sign a contract",
  "operator.merchants.profile": "Profile",
  "operator.merchants.pilot": "Pilot merchant",
  "operator.merchants.channelPartner": "Channel partner",
  "operator.merchants.direct": "Direct",
  "operator.merchants.bdStaff": "BD staff",
  "operator.merchants.afterSalesStaff": "After-sales staff",
  "operator.merchants.settlementTimezone": "Settlement timezone",
  "operator.merchants.internalRemarks": "Internal remarks",
  "operator.merchants.subscriptions": "Subscriptions",
  "operator.merchants.perMonthExpires": "{price} per month · expires {date}",
  "operator.merchants.manage": "Manage",
  "operator.merchants.nothingProvisioned":
    "Nothing provisioned yet. Sign a contract to sell this merchant a plan.",
  "operator.merchants.contracts": "Contracts",
  "operator.merchants.contractSigned": "{plan} · signed {date}",
  "operator.merchants.noContracts": "No contracts yet.",
  "operator.merchants.users": "Back-office users",
  "operator.merchants.createFailed": "Could not create the merchant.",
  "operator.merchants.create": "Create merchant",
  "operator.merchants.creating": "Creating…",
  "operator.merchants.name": "Merchant name",
  "operator.merchants.countryHint":
    "The first store is seeded from this market: {currencies}, {locales}.",
  "operator.merchants.countryHintEmpty": "Sets the market profile the first store is seeded from.",
  "operator.merchants.currencyPair": "{first} and {second}",
  "operator.merchants.selectMarket": "Select a market",
  "operator.merchants.firstStore": "First store",
  "operator.merchants.storeName": "Store name",
  "operator.merchants.webAddress": "Web address",
  "operator.merchants.webAddressHint":
    "Used by the QR ordering site. Lower case, letters, numbers and hyphens.",
  "operator.merchants.ownerUsername": "Owner username",
  "operator.merchants.ownerName": "Owner name",
  "operator.merchants.ownerEmail": "Owner email",
  "operator.merchants.ownerPassword": "Owner password",
  "operator.merchants.ownerPasswordHint":
    "At least 12 characters. Hand this over securely; it is never shown again.",

  "operator.contracts.title": "Contracts",
  "operator.contracts.description":
    "Signed from a merchant's page. A submitted contract cannot be edited — a change is a renewal, an upgrade or a new contract.",
  "operator.contracts.empty": "No contracts.",
  "operator.contracts.number": "Contract",
  "operator.contracts.signed": "Signed",
  "operator.contracts.term": "Term",
  "operator.contracts.termValue": "{months} months to {date}",
  "operator.contracts.total": "Total",
  "operator.contracts.signTitle": "Sign a contract for {merchant}",
  "operator.contracts.locked":
    "A signed contract cannot be edited. A later change is a renewal, an upgrade or a new contract.",
  "operator.contracts.sign": "Sign contract",
  "operator.contracts.signing": "Signing…",
  "operator.contracts.signFailed": "Could not sign the contract.",
  "operator.contracts.planVersion": "Plan version",
  "operator.contracts.choosePlan": "Choose a plan",
  "operator.contracts.planOption": "{name} — {price}/mo",
  "operator.contracts.billedTo": "Billed to",
  "operator.contracts.billedToHint":
    "A chain contract pools quota across every store in the group.",
  "operator.contracts.oneStore": "One store",
  "operator.contracts.wholeChain": "The whole chain",
  "operator.contracts.store": "Store",
  "operator.contracts.cycle": "Billing cycle",
  "operator.contracts.yearlyAnnual": "Yearly, billed annually",
  "operator.contracts.duration": "Duration in months",
  "operator.contracts.signDate": "Sign date",
  "operator.contracts.signDateHint": "The subscription starts on this date.",
  "operator.contracts.trialMonths": "Free trial months",
  "operator.contracts.trialMonthsHint":
    "Added on top of the term. The plan reverts automatically when the trial ends.",
  "operator.contracts.discount": "Discount",
  "operator.contracts.discountHint": "In minor units of {currency}.",
  "operator.contracts.discountHintUnknown": "In minor units of the plan's currency.",
  "operator.contracts.estimate":
    "Estimated total {total}. The server prices the contract again on submit.",

  "operator.subscriptions.title": "Subscriptions",
  "operator.subscriptions.description":
    "What each merchant currently holds, and the snapshots it was sold under.",
  "operator.subscriptions.empty": "No subscriptions.",
  "operator.subscriptions.loadFailed": "Could not load this subscription.",
  "operator.subscriptions.heading": "{merchant} — {plan}",
  "operator.subscriptions.subheading": "Tier {tier} · {scope}",
  "operator.subscriptions.invalidate": "Invalidate",
  "operator.subscriptions.renew": "Renew",
  "operator.subscriptions.changePlan": "Change plan",
  "operator.subscriptions.addon": "Add-on",
  "operator.subscriptions.term": "Term",
  "operator.subscriptions.storedStatus": "Stored status",
  "operator.subscriptions.datesDisagree": "(dates say otherwise)",
  "operator.subscriptions.starts": "Starts",
  "operator.subscriptions.trialEnds": "Trial ends",
  "operator.subscriptions.termLength": "Term length",
  "operator.subscriptions.months": "{months} months",
  "operator.subscriptions.priceSnapshot": "Price snapshot",
  "operator.subscriptions.perMonth": "{price} per month",
  "operator.subscriptions.quotaSnapshot": "Quota snapshot",
  "operator.subscriptions.unmetered": "Unmetered",
  "operator.subscriptions.quotaWithExtra": "{allowed} ({limit} + {extra} bought)",
  "operator.subscriptions.features": "Features ({count})",
  "operator.subscriptions.featurePermanent": "permanent",
  "operator.subscriptions.addons": "Add-ons",
  "operator.subscriptions.noAddons": "None bought.",
  "operator.subscriptions.addonQuota": "{resource} × {quantity}",
  "operator.subscriptions.addonUntil": "Until {date}",
  "operator.subscriptions.renewTitle": "Renew subscription",
  "operator.subscriptions.addonTitle": "Buy an add-on",
  "operator.subscriptions.apply": "Apply",
  "operator.subscriptions.applying": "Applying…",
  "operator.subscriptions.applyFailed": "Could not apply the change.",
  "operator.subscriptions.priceIt": "Price it",
  "operator.subscriptions.priceFailed": "Could not price the change.",
  "operator.subscriptions.monthsLabel": "Months",
  "operator.subscriptions.renewCycleHint":
    "An early renewal extends from the current expiry, so the remaining term is not lost.",
  "operator.subscriptions.newPlan": "New plan",
  "operator.subscriptions.newPlanHint": "Only a higher or equal tier. Downgrade is not permitted.",
  "operator.subscriptions.planTierOption": "{name} (tier {tier})",
  "operator.subscriptions.charged": "Charged {amount} for the remaining term",
  "operator.subscriptions.credited": "Credited {amount} of the current plan",
  "operator.subscriptions.net": "Net {amount}",
  "operator.subscriptions.addonType": "Add-on type",
  "operator.subscriptions.extraQuota": "Extra quota",
  "operator.subscriptions.valueAddedFeature": "Value-added feature",
  "operator.subscriptions.resource": "Resource",
  "operator.subscriptions.extraUnits": "Extra units",
  "operator.subscriptions.feature": "Feature",
  "operator.subscriptions.featureHint": "Charged for the parent term's remaining days.",
  "operator.subscriptions.chooseFeature": "Choose a feature",
  "operator.subscriptions.invalidateTitle": "Invalidate {merchant}'s subscription",
  "operator.subscriptions.invalidateWarning":
    "This is irreversible. The merchant loses every feature and quota immediately, including anything bought outright, and the subscription cannot be renewed back to life.",
  "operator.subscriptions.reason": "Reason",
  "operator.subscriptions.reasonHint":
    "Recorded against the subscription. At least ten characters.",
  "operator.subscriptions.requestCode": "Request a code",
  "operator.subscriptions.codeFailed": "Could not request a code.",
  "operator.subscriptions.invalidating": "Invalidating…",
  "operator.subscriptions.invalidateFailed": "Could not invalidate.",
  "operator.subscriptions.verificationCode": "Verification code",
  "operator.subscriptions.codeHintDev": "Development build: the code is filled in for you.",
  "operator.subscriptions.codeHintSent": "Sent to your registered channel.",

  "operator.plans.title": "Plan versions",
  "operator.plans.description":
    "Prices, quotas and feature membership are data. Editing them never changes what a signed subscription already bought.",
  "operator.plans.empty": "No plan versions.",
  "operator.plans.search": "Search plans",
  "operator.plans.yearlyPerMonth": "Yearly, per month",
  "operator.plans.annualSaving": "Annual saving",
  "operator.plans.quotas": "Quotas",
  "operator.plans.features": "Features",

  "operator.features.title": "Features",
  "operator.features.description":
    "The catalog's commercial record. Codes themselves are structural — they are what the API's gates check against — so they are not editable here.",
  "operator.features.empty": "No features.",
  "operator.features.search": "Search features",
  "operator.features.feature": "Feature",
  "operator.features.group": "Group",
  "operator.features.grades": "Sold at levels",
  "operator.features.ungraded": "On or off",
  "operator.features.valueAdded": "Sold separately",

  "operator.hardware.title": "Hardware",
  "operator.hardware.description": "Bundleable into a contract.",
  "operator.hardware.empty": "No hardware.",
  "operator.hardware.search": "Search hardware",
  "operator.hardware.name": "Hardware",
  "operator.hardware.model": "Model",

  "operator.transactions.title": "Transactions",
  "operator.transactions.description":
    "Subscription payments — not restaurant sales. This is the ledger behind the dashboard's revenue tiles.",
  "operator.transactions.empty": "No transactions.",
  "operator.transactions.serial": "Serial",
  "operator.transactions.product": "Product",
  "operator.transactions.amount": "Amount",
  "operator.transactions.paid": "Paid",
  "operator.transactions.settlement": "Settlement",
  "operator.transactions.reconciled": "Reconciled",
  "operator.transactions.markReconciled": "Mark reconciled",

  "operator.partners.title": "Channel partners",
  "operator.partners.description":
    "Resellers who earn commission on the subscriptions their merchants pay.",
  "operator.partners.empty": "No channel partners.",
  "operator.partners.search": "Search partners",
  "operator.partners.partnerId": "Partner ID",
  "operator.partners.commission": "Commission",
  "operator.partners.bonus": "First-signing bonus",
  "operator.partners.settles": "Settles",
  "operator.partners.settlesValue": "{from} → {to}",

  "operator.settlements.title": "Settlements",
  "operator.settlements.description":
    "Commission the platform owes its resellers. Completing a run needs a nominated auditor and a verification code, and there is no way to delete one.",
  "operator.settlements.empty": "No settlement runs.",
  "operator.settlements.number": "Settlement",
  "operator.settlements.period": "Period",
  "operator.settlements.periodValue": "{start} – {end}",
  "operator.settlements.gross": "Transaction amount",
  "operator.settlements.rate": "Rate",
  "operator.settlements.due": "Due",
  "operator.settlements.paid": "Paid",
  "operator.settlements.auditor": "Auditor",

  "operator.referrals.title": "Referrals",
  "operator.referrals.description": "Merchants recommended from inside other merchants' terminals.",
  "operator.referrals.empty": "No referrals yet.",
  "operator.referrals.business": "Business",
  "operator.referrals.contact": "Contact",
  "operator.referrals.phone": "Phone",
  "operator.referrals.referredBy": "Referred by",
  "operator.referrals.received": "Received",

  "operator.team.title": "Team",
  "operator.team.description": "Platform staff and channel-partner accounts.",
  "operator.team.empty": "No operator accounts.",
  "operator.team.name": "Name",
  "operator.team.username": "Username",
  "operator.team.email": "Email",
  "operator.team.role": "Role",
  "operator.team.scope": "Scope",
  "operator.team.ownPartner": "Own partner's merchants",
  "operator.team.platform": "Platform",
  "operator.team.search": "Search team",
  "operator.team.new": "New operator",
  "operator.team.edit": "Edit operator",
  "operator.team.record": "Record",
  "operator.team.password": "Password",
  "operator.team.passwordHint": "At least twelve characters.",
  "operator.team.setPassword": "Set password",
  "operator.team.setPasswordHint":
    "Signs them out everywhere, including you, if you are resetting your own password.",
  "operator.team.deleteTitle": "Remove this operator?",
  "operator.team.deleteBody":
    "They can no longer sign in, and their username and email become free for a new account to take. Their name stays on the merchants they signed and on every audit row they wrote.",

  "operator.roles.title": "Roles",
  "operator.roles.description":
    "Operator roles. Only operator permissions can be granted — a merchant permission here would be a grant nothing ever checks.",
  "operator.roles.empty": "No roles.",
  "operator.roles.role": "Role",
  "operator.roles.seeded": "Seeded",
  "operator.roles.members": "Members",
  "operator.roles.permissions": "Permissions",
  "operator.roles.granted": "{count} granted",
  "operator.roles.new": "New role",
  "operator.roles.name": "Name",
  "operator.roles.note": "Notes",
  "operator.roles.deleteTitle": "Delete this role?",
  "operator.roles.deleteBody":
    "Nobody can be assigned it afterwards, and its permissions are removed. Its code becomes free to reuse.",
  "operator.roles.noneGranted": "No permissions granted.",
  "operator.roles.catalogueFailed":
    "Could not load the permission catalogue, so each grant is shown by its key.",
  "operator.roles.reassignPrompt":
    "Currently held by {count}. Move them to another role, then delete.",
  "operator.roles.reassignTo": "Move them to",
  "operator.roles.chooseRole": "Choose a role",
  "operator.roles.reassignConfirm": "Move and delete",

  "operator.audit.title": "Audit log",
  "operator.audit.description":
    "Every operator action that moved money or changed a price. Append-only — there is no write endpoint.",
  "operator.audit.empty": "Nothing recorded yet.",
  "operator.audit.when": "When",
  "operator.audit.who": "Who",
  "operator.audit.action": "Action",
  "operator.audit.entity": "Entity",
  "operator.audit.changedFields": "Changed fields",

  /* Enums the operator console renders. Each opts its schema enum into
     `enum-messages.test.ts`, which is why every value of each is here. */
  "chainStatus.active": "Active",
  "chainStatus.suspended": "Suspended",
  "chainStatus.invalidated": "Invalidated",

  "storeStatus.active": "Active",
  "storeStatus.suspended": "Suspended",
  "storeStatus.closed": "Closed",

  "userStatus.active": "Active",
  "userStatus.disabled": "Disabled",

  "partnerStatus.active": "Active",
  "partnerStatus.suspended": "Suspended",

  "planStatus.active": "Active",
  "planStatus.invalidated": "Withdrawn",

  "contractStatus.draft": "Draft",
  "contractStatus.submitted": "Submitted",
  "contractStatus.active": "Active",
  "contractStatus.completed": "Completed",
  "contractStatus.cancelled": "Cancelled",

  "billingScope.store": "Store",
  "billingScope.chain": "Chain",

  "productKind.plan": "Plan",
  "productKind.addon": "Add-on",
  "productKind.hardware": "Hardware",
  "productKind.package": "Package",

  "transactionSettlementStatus.pending": "Pending",
  "transactionSettlementStatus.settled": "Settled",

  "partnerSettlementStatus.draft": "Draft",
  "partnerSettlementStatus.pending_audit": "Pending audit",
  "partnerSettlementStatus.completed": "Completed",
  "partnerSettlementStatus.failed": "Failed",

  "referralStatus.new": "New",
  "referralStatus.contacted": "Contacted",
  "referralStatus.converted": "Converted",
  "referralStatus.rejected": "Rejected",

  "featureGroup.cashier": "Cashier",
  "featureGroup.data": "Reporting",
  "featureGroup.store_settings": "Store settings",
  "featureGroup.marketing": "Marketing",
  "featureGroup.item_management": "Menu",
  "featureGroup.order_handling": "Orders",
  "featureGroup.settlement": "Settlement",
  "featureGroup.inventory": "Inventory",
  "featureGroup.reservation": "Reservations",
  "featureGroup.chain": "Chain",

  "grade.basic": "Basic",
  "grade.premium": "Premium",
  // Permission labels — role screens render the catalogue's key through here
  // rather than the catalogue's English `name`.
  "permission.items.read": "View items",
  "permission.items.write": "Create and edit items",
  "permission.items.delete": "Delete items",
  "permission.items.price": "Change item prices",
  "permission.categories.read": "View categories",
  "permission.categories.write": "Create and edit categories",
  "permission.categories.delete": "Delete categories",
  "permission.modifiers.read": "View specifications, toppings and tags",
  "permission.modifiers.write": "Edit specifications, toppings and tags",
  "permission.modifiers.delete": "Delete specifications, toppings and tags",
  "permission.tables.read": "View tables and areas",
  "permission.tables.write": "Create and edit tables and areas",
  "permission.tables.delete": "Delete tables and areas",
  "permission.orders.read": "View orders",
  "permission.orders.write": "Take and amend orders",
  "permission.orders.export": "Export orders",
  "permission.orders.discount": "Apply a discount",
  "permission.orders.void": "Void an order or a line",
  "permission.orders.refund": "Refund",
  "permission.orders.reverse": "Reverse a completed check-out",
  "permission.orders.tax_exempt": "Settle an order tax-free",
  "permission.orders.tip_adjust": "Adjust a tip after payment",
  "permission.hr.read": "View jobs and timesheets",
  "permission.hr.write": "Manage jobs and correct timesheets",
  "permission.hr.clock": "Clock in and out",
  "permission.hr.tip_finalise": "Finalise a tip distribution",
  "permission.chain.royalty": "Set royalties and raise charges",
  "permission.reservations.read": "View bookings and the waitlist",
  "permission.reservations.write": "Take and seat bookings",
  "permission.giftcards.read": "Look up a gift card",
  "permission.giftcards.write": "Issue and void gift cards",
  "permission.settlement.pay": "Take payment",
  "permission.settlement.drawer": "Open the cash drawer",
  "permission.shifts.read": "View shift records",
  "permission.shifts.write": "Open and hand over a shift",
  "permission.staff.read": "View staff",
  "permission.staff.write": "Create and edit staff",
  "permission.staff.delete": "Delete staff",
  "permission.roles.read": "View roles",
  "permission.roles.write": "Create and edit roles",
  "permission.settings.read": "View store settings",
  "permission.settings.store": "Edit the store profile",
  "permission.settings.tax": "Edit tax rules",
  "permission.settings.charges": "Edit service charge and table fee",
  "permission.settings.currency": "Edit currency and exchange rate",
  "permission.settings.payment_methods": "Edit payment methods",
  "permission.printers.read": "View printers and print logs",
  "permission.printers.write": "Edit, pair and unpair printers",
  "permission.printers.reprint": "Reprint a document",
  "permission.terminals.pair": "Pair a till",
  "permission.terminals.assign_staff": "Put someone on a till",
  "permission.templates.read": "View receipt templates",
  "permission.templates.write": "Edit receipt templates",
  "permission.banners.read": "View banners",
  "permission.banners.write": "Create and edit banners",
  "permission.banners.delete": "Delete banners",
  "permission.marketing.read": "View coupons and promotions",
  "permission.marketing.write": "Create and edit coupons and promotions",
  "permission.members.read": "View members",
  "permission.members.write": "Create and edit members",
  "permission.members.balance": "Top up and adjust a member balance",
  "permission.members.points": "Adjust member points",
  "permission.reports.read": "View reports",
  "permission.reports.export": "Export reports",
  "permission.reports.cost": "View cost and profit reports",
  "permission.audit.read": "View the operation log",
  "permission.billing.read": "View the subscription",
  "permission.billing.manage": "Sign, renew and buy add-ons",
  "permission.chain.read": "View chain rollups",
  "permission.chain.write": "Configure stores in the chain",
  "permission.admin.merchants.read": "View merchants",
  "permission.admin.merchants.write": "Create and edit merchants",
  "permission.admin.plans.read": "View plan versions and features",
  "permission.admin.plans.write": "Edit plan versions and features",
  "permission.admin.contracts.read": "View contracts",
  "permission.admin.contracts.sign": "Sign and renew contracts",
  "permission.admin.subscriptions.read": "View subscriptions",
  "permission.admin.subscriptions.write": "Provision, upgrade and renew subscriptions",
  "permission.admin.subscriptions.invalidate": "Invalidate a subscription",
  "permission.admin.hardware.read": "View hardware",
  "permission.admin.hardware.write": "Edit hardware",
  "permission.admin.partners.read": "View channel partners",
  "permission.admin.partners.write": "Edit channel partners",
  "permission.admin.settlements.read": "View partner settlements",
  "permission.admin.settlements.complete": "Complete a partner settlement",
  "permission.admin.reconciliation.read": "View reconciliation",
  "permission.admin.team.read": "View the operator team",
  "permission.admin.team.write": "Edit the operator team and its roles",
  "permission.admin.dashboard.read": "View the operator dashboard",
  "permission.admin.referrals.read": "View referrals",
  "permission.admin.referrals.write": "Update referral status and ownership",
  "permission.psi.warehouse.read": "View warehouses",
  "permission.psi.warehouse.write": "Create and edit warehouses",
  "permission.psi.supply.read": "View supplies, units, categories and brands",
  "permission.psi.supply.write": "Create and edit supplies, units, categories and brands",
  "permission.psi.supplier.read": "View suppliers",
  "permission.psi.supplier.write": "Create and edit suppliers",
  "permission.psi.purchase.read": "View purchase orders",
  "permission.psi.purchase.write": "Raise and edit purchase orders",
  "permission.psi.purchase.approve": "Approve or void a purchase order",
  "permission.psi.inbound.read": "View inbound tickets",
  "permission.psi.inbound.write": "Raise and edit inbound tickets",
  "permission.psi.inbound.approve": "Approve or reject an inbound ticket",
  "permission.psi.outbound.read": "View outbound orders",
  "permission.psi.outbound.write": "Raise and edit outbound orders",
  "permission.psi.outbound.approve": "Approve an outbound order",
  "permission.psi.count.read": "View stock counts",
  "permission.psi.count.write": "Run a stock count",
  "permission.psi.count.adjust": "Post a stock adjustment",
  "permission.psi.stock.read": "View stock balances and batches",
  "permission.psi.recipe.write": "Link menu items to supplies",
  "permission.psi.requisition.approve": "Review a substore's requisition",

  "operationAction.billing.renew": "Subscription renewed",
  "operationAction.campaign.create": "Campaign created",
  "operationAction.campaign.delete": "Campaign deleted",
  "operationAction.campaign.update": "Campaign updated",
  "operationAction.cancel": "Booking cancelled",
  "operationAction.chain.capabilities_changed": "Store capabilities changed",
  "operationAction.chain.menu_pushed": "Menu pushed to stores",
  "operationAction.chain.requisition_approved": "Requisition approved",
  "operationAction.chain.requisition_rejected": "Requisition rejected",
  "operationAction.chain.store_added": "Store added to chain",
  "operationAction.chain.user_access_changed": "Store access changed",
  "operationAction.cost.create": "Cost added",
  "operationAction.cost.delete": "Cost deleted",
  "operationAction.cost.update": "Cost updated",
  "operationAction.coupon.issue": "Coupon issued",
  "operationAction.coupon_template.copy": "Coupon template copied",
  "operationAction.coupon_template.create": "Coupon template created",
  "operationAction.coupon_template.update": "Coupon template updated",
  "operationAction.create": "Booking created",
  "operationAction.gift_card.issue": "Gift card issued",
  "operationAction.gift_card.top_up": "Gift card topped up",
  "operationAction.gift_card.void": "Gift card voided",
  "operationAction.inventory.inbound_approved": "Inbound ticket approved",
  "operationAction.inventory.inbound_rejected": "Inbound ticket rejected",
  "operationAction.inventory.outbound_issued": "Outbound order issued",
  "operationAction.inventory.purchase_notified": "Purchase order sent to supplier",
  "operationAction.inventory.purchase_raised": "Purchase order raised",
  "operationAction.inventory.purchase_received": "Purchase order received",
  "operationAction.inventory.purchase_voided": "Purchase order voided",
  "operationAction.inventory.recipe_changed": "Item recipe changed",
  "operationAction.inventory.stock_adjusted": "Stock adjusted",
  "operationAction.inventory.supplier_prices_changed": "Supplier prices changed",
  "operationAction.inventory.transfer_accepted": "Transfer accepted",
  "operationAction.inventory.transfer_rejected": "Transfer rejected",
  "operationAction.item.batch": "Items updated in bulk",
  "operationAction.item.create": "Item added",
  "operationAction.item.delete": "Item deleted",
  "operationAction.item.meal_set_changed": "Meal set changed",
  "operationAction.item.update": "Item updated",
  "operationAction.job.create": "Job added",
  "operationAction.job.delete": "Job deleted",
  "operationAction.job.update": "Job updated",
  "operationAction.no_show": "Booking marked no-show",
  "operationAction.notifications.update": "Alert settings updated",
  "operationAction.payment_method.create": "Payment method added",
  "operationAction.payment_method.delete": "Payment method deleted",
  "operationAction.print.reprint": "Receipt reprinted",
  "operationAction.printer.bind": "Printer assigned",
  "operationAction.printer.create": "Printer added",
  "operationAction.printer.unbind": "Printer unassigned",
  "operationAction.promotion.create": "Promotion created",
  "operationAction.promotion.delete": "Promotion deleted",
  "operationAction.promotion.update": "Promotion updated",
  "operationAction.receipt_template.update": "Receipt template updated",
  "operationAction.role.create": "Role created",
  "operationAction.role.update": "Role updated",
  "operationAction.royalty.run": "Royalty charges run",
  "operationAction.royalty_charge.finalise": "Royalty charge finalised",
  "operationAction.royalty_scheme.create": "Royalty scheme created",
  "operationAction.royalty_scheme.set_stores": "Royalty scheme stores changed",
  "operationAction.royalty_scheme.update": "Royalty scheme updated",
  "operationAction.seat": "Party seated",
  "operationAction.service_charge_rate.create": "Service charge rate added",
  "operationAction.service_charge_rate.delete": "Service charge rate deleted",
  "operationAction.service_charge_rate.update": "Service charge rate updated",
  "operationAction.settings.delivery_providers_changed": "Delivery providers changed",
  "operationAction.settings.invoice_changed": "Invoice numbering changed",
  "operationAction.settlement.drawer_opened": "Cash drawer opened",
  "operationAction.shift.close": "Shift closed",
  "operationAction.specification_group.create": "Specification group created",
  "operationAction.specification_group.delete": "Specification group deleted",
  "operationAction.specification_group.update": "Specification group updated",
  "operationAction.staff.create": "Employee added",
  "operationAction.staff.delete": "Employee deleted",
  "operationAction.staff.photo_delete": "Employee photo removed",
  "operationAction.staff.photo_set": "Employee photo set",
  "operationAction.staff.reset_pin": "Employee PIN reset",
  "operationAction.staff.take_till": "Took a till by scanning",
  "operationAction.staff.assign_till": "Put on a till by a manager",
  "operationAction.staff.update": "Employee updated",
  "operationAction.staff.set_password": "Employee password set",
  "operationAction.store_group.create": "Store group created",
  "operationAction.store_group.delete": "Store group deleted",
  "operationAction.store_group.set_stores": "Store group stores changed",
  "operationAction.store_group.update": "Store group updated",
  "operationAction.store_profile.update": "Store profile updated",
  "operationAction.table_fee.update": "Table fee updated",
  "operationAction.tax_rate.create": "Tax rate added",
  "operationAction.tax_rate.delete": "Tax rate deleted",
  "operationAction.tax_rate.update": "Tax rate updated",
  "operationAction.tax_settings.update": "Tax settings updated",
  "operationAction.time_entry.correct": "Timesheet corrected",
  "operationAction.tip_distribution.finalise": "Tip distribution finalised",
  "operationAction.tip_pool.create": "Tip pool created",
  "operationAction.tip_pool.delete": "Tip pool deleted",
  "operationAction.tip_pool.run": "Tip pool run",
  "operationAction.tip_pool.set_jobs": "Tip pool jobs changed",
  "operationAction.tip_pool.update": "Tip pool updated",
  "operationAction.tip_settings.update": "Tip settings updated",
  "operationAction.topping_template.create": "Topping template created",
  "operationAction.topping_template.delete": "Topping template deleted",
  "operationAction.topping_template.update": "Topping template updated",
  "operationAction.topup_tier.create": "Top-up tier created",
  "operationAction.topup_tier.delete": "Top-up tier deleted",
  "operationAction.trading_config.update": "Trading hours updated",
} as const;

export type MessageKey = keyof typeof en;
export type MessagePack = Record<MessageKey, string>;
